import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  MapPin, Camera, Calendar, MessageCircle, ArrowRight,
  CheckCircle, Shield, Star, Eye, Plus, Building2,
  GraduationCap, Sparkles, Download, Smartphone, Target, Check,
  Play, Receipt, CreditCard, TrendingUp, Clock, Users, Award, AlertTriangle
} from "lucide-react";
import { WA_LINK, CALENDLY, ContactSection } from "@/Layout";
import IntroSplash from "@/components/IntroSplash";
import VirtualTourSection from "@/components/sections/VirtualTourSection";
import TimewarpVideoSection from "@/components/sections/TimewarpVideoSection";
import AnimatedShortComponent from "@/components/AnimatedShort";
import ProofSection from "@/components/sections/ProofSection";
import ChiFaLeFotoSection from "@/components/sections/ChiFaLeFotoSection";
import EcologiaDigitaleArticle from "@/components/EcologiaDigitaleArticle";
import { FullLogo, NickLogo } from "@/components/BrandLogo";
import { ArrowUp } from "lucide-react";

// Section divider — alternanza logo luxury e S.com Cares
let dividerCount = 0;
const BrandDivider = () => {
  const idx = dividerCount++;
  const isLuxury = idx % 2 === 0;
  return (
    <div className="py-6 flex justify-center" data-testid={`brand-divider-${idx}`}>
      {isLuxury ? (
        <span className="text-lg font-black tracking-tight" style={{ fontFamily: "'Poppins', sans-serif", background: 'linear-gradient(135deg, #D4AF37 0%, #F5E6A3 40%, #D4AF37 60%, #B8942E 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
          schedabusiness.com
        </span>
      ) : (
        <div className="flex items-center gap-1.5">
          <NickLogo size={24} showPayoff={false} />
          <span className="text-sm font-black italic" style={{ fontFamily: "'Poppins',sans-serif", background: 'linear-gradient(135deg, #D4AF37, #F5E6A3, #D4AF37, #B8942E)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Cares</span>
        </div>
      )}
    </div>
  );
};

// Scroll to top button — stile WhatsApp (nero + verde matrix)
const ScrollToTop = () => {
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 w-11 h-11 rounded-full bg-black border-2 border-[#25D366] text-[#25D366] flex items-center justify-center hover:bg-[#25D366] hover:text-black transition-all duration-300 hover:scale-110 shadow-[0_0_15px_rgba(37,211,102,0.3)]"
      data-testid="scroll-to-top"
      aria-label="Torna in alto"
    >
      <ArrowUp className="w-5 h-5" />
    </button>
  );
};

// 6 Quick Nav CTA Cards — Google color scale
const QUICK_NAV = [
  { label: "Deducibile 100%", sub: "TAEG 0% · 3 rate PayPal", href: "https://www.schedabusiness.it/#deducibile", color: "#4285F4", external: true },
  { label: "Start", sub: "Pacchetto base assistenza", href: "/start", color: "#EA4335" },
  { label: "Plus", sub: "Assistenza completa + bonus", href: "/plus", color: "#FBBC04", dark: true },
  { label: "Virtual Tour", sub: "499€ · Timewarp IN REGALO", href: "/deducibile100", color: "#0D0D0D", dark: false, gradientBorder: true },
  { label: "Rooftop", sub: "Premium · tutto incluso", href: "/rooftop", color: "#34A853" },
  { label: "Formazione .it", sub: "Corsi 100% detraibili", href: "https://www.schedabusiness.it", color: "#EA4335", external: true },
];

const QuickNavCards = () => (
  <section className="py-6 bg-[#0c1220]" data-testid="quick-nav-section">
    <div className="max-w-5xl mx-auto px-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {QUICK_NAV.map((item, i) => {
          const Tag = item.external ? "a" : Link;
          const props = item.external ? { href: item.href, target: "_blank", rel: "noopener noreferrer" } : { to: item.href };
          if (item.gradientBorder) {
            return (
              <Tag
                key={i}
                {...props}
                className="group relative rounded-2xl p-[2px] text-center transition-all duration-300 hover:scale-105 hover:-translate-y-1 overflow-hidden"
                data-testid={`quick-nav-${i}`}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#4285F4] via-[#EA4335] via-[#FBBC04] to-[#34A853] opacity-70 group-hover:opacity-100 transition-opacity rounded-2xl"></div>
                <div className="relative bg-[#0c1220] rounded-[14px] p-3 sm:p-4 h-full">
                  <div className="w-8 h-8 mx-auto mb-2 rounded-lg flex items-center justify-center bg-gradient-to-r from-[#4285F4] via-[#FBBC04] to-[#34A853]">
                    <span className="text-xs font-black text-white">{i + 1}</span>
                  </div>
                  <h3 className="text-white text-xs sm:text-sm font-bold leading-tight mb-0.5">{item.label}</h3>
                  <p className="text-gray-400 text-[9px] sm:text-[10px] leading-tight">{item.sub}</p>
                </div>
              </Tag>
            );
          }
          return (
            <Tag
              key={i}
              {...props}
              className="group relative rounded-2xl p-3 sm:p-4 text-center transition-all duration-300 hover:scale-105 hover:-translate-y-1 overflow-hidden"
              style={{
                background: `linear-gradient(135deg, ${item.color}22, ${item.color}11)`,
                border: `1.5px solid ${item.color}44`,
              }}
              data-testid={`quick-nav-${i}`}
            >
              <div className="w-8 h-8 mx-auto mb-2 rounded-lg flex items-center justify-center" style={{ background: item.color }}>
                <span className={`text-xs font-black ${item.dark ? 'text-gray-900' : 'text-white'}`}>{i + 1}</span>
              </div>
              <h3 className="text-white text-xs sm:text-sm font-bold leading-tight mb-0.5">{item.label}</h3>
              <p className="text-gray-400 text-[9px] sm:text-[10px] leading-tight">{item.sub}</p>
            </Tag>
          );
        })}
      </div>
    </div>
  </section>
);

// Sub-brand icons as custom SVG components
const IconStart = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polygon points="10,8 16,12 10,16" fill="currentColor" stroke="none" />
  </svg>
);

const IconPlus = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const IconRooftop = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 21h18" />
    <path d="M5 21V7l7-4 7 4v14" />
    <path d="M9 21v-6h6v6" />
    <path d="M10 3v2" />
    <path d="M14 3v2" />
    <line x1="9" y1="10" x2="9" y2="10.01" />
    <line x1="15" y1="10" x2="15" y2="10.01" />
  </svg>
);

const FOTOGRAFO_IMG = "https://customer-assets.emergentagent.com/job_031c0675-bd53-49c3-b1d2-b66f6ab51727/artifacts/w8tue8sq_adesivogoogle.jpg";

const PayPalLogo = ({ className = "w-4 h-3" }) => (
  <svg className={className} viewBox="0 0 101 32" fill="currentColor">
    <path d="M12.2 4.6h-7c-.5 0-.9.3-1 .8L1.5 24c-.1.4.2.7.6.7h3.4c.5 0 .9-.3 1-.8l.8-5.1c.1-.5.5-.8 1-.8h2.3c4.7 0 7.5-2.3 8.2-6.8.3-2 0-3.5-.9-4.6C16.8 5.3 14.8 4.6 12.2 4.6z"/>
    <path d="M39.6 4.6h-7c-.5 0-.9.3-1 .8l-2.7 18.6c-.1.4.2.7.6.7h3.6c.3 0 .6-.2.7-.6l.8-5.3c.1-.5.5-.8 1-.8h2.3c4.7 0 7.5-2.3 8.2-6.8.3-2 0-3.5-.9-4.6C44.2 5.3 42.2 4.6 39.6 4.6z"/>
  </svg>
);

// AI-generated images (Nano Banana / Gemini) + Stock
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const AI_IMG = {
  hero: `${API_BASE}/api/images/hero_banner.png`,
  heroColle: 'https://customer-assets.emergentagent.com/job_ae3da24b-c94d-480d-9af8-171d8323bf32/artifacts/x89wca9d_chrome_FvEL9MTQnX.png',
  start: `${API_BASE}/api/images/start_package.png`,
  plus: `${API_BASE}/api/images/plus_package.png`,
  rooftop: `${API_BASE}/api/images/rooftop_package.png`,
  virtualTour: `${API_BASE}/api/images/sbp360_vr.png`,
  puzzle: `${API_BASE}/api/images/creative_surprise.png`,
  growth: 'https://static.prod-images.emergentagent.com/jobs/43fdf589-9509-408d-b8b7-6db9bf6a0fa9/images/75cfededa9b4261cfa2f280021680d7ce940a79707119ce5de0d89e0885ae364.png',
};

// Simplified Hero for the 4-door homepage
const HeroSection = () => {
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const h = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);

  const starsFar = [
    {t:2,l:8,s:3,d:'2.1s',del:'0s'},{t:6,l:82,s:2.5,d:'3.5s',del:'1s'},{t:14,l:3,s:2,d:'4s',del:'0.5s'},
    {t:4,l:42,s:2.5,d:'2.8s',del:'0.3s'},{t:22,l:68,s:2,d:'3.2s',del:'1.5s'},{t:32,l:12,s:3,d:'2.5s',del:'0.8s'},
    {t:38,l:88,s:2,d:'3.8s',del:'0.2s'},{t:48,l:28,s:2.5,d:'2.9s',del:'1.2s'},{t:52,l:58,s:2,d:'4.2s',del:'0.7s'},
    {t:62,l:6,s:3,d:'3.1s',del:'0.4s'},{t:68,l:48,s:2,d:'2.7s',del:'1.8s'},{t:74,l:72,s:2.5,d:'3.6s',del:'0.6s'},
    {t:1,l:62,s:2,d:'4.5s',del:'0.1s'},{t:18,l:38,s:3,d:'3s',del:'0.9s'},{t:42,l:2,s:2,d:'3.3s',del:'1.1s'},
  ];
  const starsNear = [
    {t:56,l:46,s:3,d:'2.3s',del:'1.6s'},{t:28,l:92,s:2.5,d:'4.1s',del:'0.4s'},{t:58,l:20,s:3,d:'3.7s',del:'1.3s'},
    {t:82,l:36,s:2.5,d:'2.6s',del:'0.8s'},{t:10,l:56,s:3.5,d:'3.4s',del:'0.2s'},{t:26,l:24,s:2.5,d:'4.3s',del:'1.4s'},
    {t:44,l:76,s:3,d:'2.4s',del:'0.5s'},{t:72,l:60,s:2.5,d:'3.9s',del:'1.7s'},{t:16,l:94,s:3.5,d:'2.2s',del:'0.3s'},
    {t:86,l:14,s:2.5,d:'3s',del:'0.6s'},{t:90,l:52,s:3,d:'2.8s',del:'1s'},{t:78,l:84,s:2.5,d:'3.5s',del:'0.4s'},
    {t:36,l:34,s:3.5,d:'4s',del:'0.7s'},{t:92,l:78,s:2.5,d:'2.5s',del:'1.2s'},{t:8,l:16,s:3,d:'3.3s',del:'0.9s'},
  ];

  return (
  <section className="relative py-12 lg:py-16 overflow-hidden">
    {/* Colle Val d'Elsa background — nero solido in cima fino a "Soluzioni...tour" */}
    <div className="absolute inset-0">
      <img src={AI_IMG.heroColle} alt="Colle Val d'Elsa" className="w-full h-full object-cover object-bottom" style={{ clipPath: 'inset(40% 0 0 0)' }} />
      <div className="absolute inset-0 bg-[#0c1220]" style={{ clipPath: 'inset(0 0 60% 0)' }}></div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#0c1220]/95 via-[#0c1220]/90 to-[#0c1220]/60"></div>
    </div>
    <div className="absolute left-4 top-20 opacity-10 hidden md:block">
      <MapPin className="w-32 h-32 text-[#ff6b35]" />
    </div>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div className="text-left">
          <Badge className="bg-green-900/30 text-green-400 border-green-700/30 mb-4 rounded-full px-4 py-1">
            <Camera className="w-4 h-4 mr-2" />
            Fotografo Street View Certificato dal 2016
          </Badge>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight font-google">
            <span className="text-[#ff6b35]">Soluzioni mirate</span>, assistenza e{" "}
            <span className="font-black italic" style={{ fontFamily: "'Poppins',sans-serif", background: 'linear-gradient(135deg, #D4AF37, #F5E6A3, #D4AF37, #B8942E)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>virtual tour</span>
          </h1>
          <div className="relative overflow-hidden rounded-2xl p-[2px] mb-8">
            <div className="absolute inset-0 bg-gradient-to-r from-[#4285F4] via-[#EA4335] via-[#FBBC04] to-[#34A853] opacity-40"></div>
            <div className="relative bg-[#0c1220] rounded-[14px] px-5 py-4">
              <p className="text-lg text-gray-300 leading-relaxed">
                Sono <strong className="text-white">Riccardo Rossi</strong>, il <strong>TUO</strong> consulente
                <strong> speciale</strong>. Profilo sospeso? Recensioni da gestire? Virtual tour per dare
                <strong> visibilità</strong> alla <strong>TUA</strong> attivita'? <strong>Ci penso io.</strong>
              </p>
            </div>
          </div>
          <div className="relative overflow-hidden rounded-2xl p-[1.5px] mb-6">
            <div className="absolute inset-0 bg-gradient-to-r from-[#FBBC04] via-[#34A853] via-[#4285F4] to-[#EA4335] opacity-50"></div>
            <div className="relative bg-[#0c1220] rounded-[13px] px-4 py-3">
              <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-gray-400">
                <span className="flex items-center gap-1.5"><Star className="w-3.5 h-3.5 text-[#FBBC04] fill-[#FBBC04]" /><strong className="text-white">51 recensioni reali</strong> tutte a 5 stelle</span>
                <span className="flex items-center gap-1.5"><Camera className="w-3.5 h-3.5 text-[#4285F4]" /><strong className="text-white">7.000+</strong> foto pubblicate</span>
                <span className="flex items-center gap-1.5"><Eye className="w-3.5 h-3.5 text-[#34A853]" /><strong className="text-white">52 milioni</strong> di visualizzazioni</span>
              </div>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mb-2">
            {/* CTA Assistenza + Virtual Tour — arcobaleno Google su nero */}
            <button
              onClick={() => document.getElementById('pacchetti')?.scrollIntoView({ behavior: 'smooth' })}
              className="relative overflow-hidden group rounded-2xl p-[2px] transition-transform hover:scale-[1.03]"
              data-testid="hero-cta-assistenza"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#4285F4] via-[#EA4335] via-[#FBBC04] to-[#34A853] opacity-80 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative bg-[#0c1220] rounded-[14px] px-5 py-4 text-left">
                <span className="text-white font-black text-base block">Assistenza + <span className="font-black italic" style={{ background: 'linear-gradient(135deg, #D4AF37, #F5E6A3, #D4AF37)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Virtual Tour</span></span>
                <span className="text-gray-400 text-xs">Sospensioni, recensioni, visibilita' 360</span>
              </div>
            </button>
            {/* CTA Formazione — cornice arcobaleno Google (come Assistenza) */}
            <a
              href="https://www.schedabusiness.it"
              target="_blank"
              rel="noopener noreferrer"
              className="relative overflow-hidden group rounded-2xl p-[2px] transition-transform hover:scale-[1.03] block"
              data-testid="hero-cta-formazione"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#34A853] via-[#FBBC04] via-[#EA4335] to-[#4285F4] opacity-60 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative bg-[#0c1220] rounded-[14px] px-5 py-4 text-left">
                <span className="text-white font-black text-base block">Formazione 100% Detraibile</span>
                <span className="text-gray-400 text-xs">Corsi da €299 · TAEG 0% · 3 rate PayPal</span>
              </div>
            </a>
          </div>
          {/* PayPal 3 rate TAEG 0% — tra le CTA */}
          <div className="flex items-center justify-center gap-2 mb-4" data-testid="paypal-3-rate-badge">
            <svg className="h-5 w-auto" viewBox="0 0 24 24" fill="none">
              <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.771.771 0 0 1 .761-.652h6.199c2.063 0 3.543.585 4.268 1.694.335.516.538 1.087.617 1.742.083.677.032 1.478-.157 2.398l-.013.063v.564l.44.249c.372.21.67.455.893.735.37.464.607 1.042.7 1.715.096.693.037 1.498-.173 2.393-.241 1.028-.635 1.922-1.171 2.655a5.292 5.292 0 0 1-1.539 1.546c-.593.39-1.298.672-2.097.84-.769.161-1.64.245-2.582.245h-.614a1.793 1.793 0 0 0-1.77 1.513l-.046.244-.77 4.88-.036.174a.164.164 0 0 1-.047.1.13.13 0 0 1-.078.028z" fill="#253B80"/>
              <path d="M18.36 7.667c-1 5.155-4.426 6.933-8.801 6.933h-2.228a1.08 1.08 0 0 0-1.07.918L5.09 22.83l-.323 2.054a.57.57 0 0 0 .563.66h3.952a.95.95 0 0 0 .94-.803l.038-.201.744-4.728.048-.26a.951.951 0 0 1 .94-.804h.591c3.83 0 6.828-1.556 7.704-6.063.366-1.882.176-3.453-.793-4.558a3.793 3.793 0 0 0-1.082-.835z" fill="#179BD7"/>
            </svg>
            <span className="text-gray-400 text-xs font-medium">3 rate · <span className="text-white font-bold">TAEG 0%</span> · su tutti i pacchetti</span>
          </div>
        </div>
        <div className="relative flex justify-center">
          <style>{`
            @keyframes orbit { from { transform: rotateX(55deg) rotateZ(0deg); } to { transform: rotateX(55deg) rotateZ(360deg); } }
            @keyframes counter-orbit { from { transform: rotateZ(0deg) rotateX(-55deg); } to { transform: rotateZ(-360deg) rotateX(-55deg); } }
            @keyframes orbit-slow { from { transform: rotateX(55deg) rotateZ(0deg); } to { transform: rotateX(55deg) rotateZ(360deg); } }
            @keyframes twinkle { 0%,100% { opacity: 0.2; } 50% { opacity: 1; } }
            @keyframes aurora { 0%,100% { opacity: 0.4; box-shadow: 0 0 4px #34A853, 0 0 8px #ff69b4; } 50% { opacity: 0.9; box-shadow: 0 0 8px #34A853, 0 0 16px #ff69b4; } }
            @keyframes nebula-drift { 0%,100% { opacity: 0.25; transform: scale(1); } 50% { opacity: 0.45; transform: scale(1.1); } }
            @keyframes float-dust { 0%,100% { transform: translateY(0) translateX(0); } 50% { transform: translateY(-6px) translateX(3px); } }
            @keyframes blink-data { 0%,49% { opacity: 1; } 50%,100% { opacity: 0.3; } }
            .orbit-ring { animation: orbit 50s linear infinite; transform-style: preserve-3d; }
            .orbit-item { animation: counter-orbit 50s linear infinite; transform-style: preserve-3d; }
            .orbit-ring-fast { animation: orbit 35s linear infinite; transform-style: preserve-3d; }
            .orbit-item-fast { animation: counter-orbit 35s linear infinite; transform-style: preserve-3d; }
            .orbit-ring-earth { animation: orbit-slow 120s linear infinite; transform-style: preserve-3d; }
            .orbit-item-earth { animation: counter-orbit 120s linear infinite; transform-style: preserve-3d; }
            .star { animation: twinkle var(--tw-dur, 3s) ease-in-out infinite; animation-delay: var(--tw-del, 0s); }
            .aurora-dot { animation: aurora 4s ease-in-out infinite; }
            .nebula { animation: nebula-drift 8s ease-in-out infinite; }
            .blink { animation: blink-data 2s step-end infinite; }
            /* === 3D GLASS MARBLE — lune === */
            .marble-moon {
              position: relative;
              box-shadow:
                0 10px 28px rgba(0,0,0,0.7),
                inset -7px -7px 16px rgba(0,0,0,0.55),
                inset 5px 4px 12px rgba(255,255,255,0.2);
            }
            .marble-moon::after {
              content: '';
              position: absolute;
              inset: 0;
              border-radius: 50%;
              background:
                radial-gradient(circle at 28% 18%, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.25) 15%, transparent 45%),
                radial-gradient(circle at 72% 78%, rgba(0,0,0,0.4) 0%, transparent 38%);
              pointer-events: none;
              z-index: 20;
            }
            /* === LOGO UNIPOSCA stroke === */
            .uniposca-stroke {
              -webkit-text-stroke: 1.5px #000;
              paint-order: stroke fill;
              text-shadow: 1px 1px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 2px 2px 4px rgba(0,0,0,0.8);
            }
          `}</style>
          <div className="relative" style={{ width: '540px', height: '540px', perspective: '1000px', transformStyle: 'preserve-3d' }}>

            {/* ====== DEEP SPACE — PARALLAX STARS ====== */}
            {/* Far stars — barely move on scroll (deep space) */}
            <div className="absolute inset-0" style={{ transform: `translate3d(0, ${scrollY * 0.02}px, 0)`, willChange: 'transform' }}>
              {starsFar.map((s,i) => (
                <div key={`f${i}`} className="star absolute rounded-full bg-white" style={{ top:`${s.t}%`, left:`${s.l}%`, width:`${s.s}px`, height:`${s.s}px`, '--tw-dur':s.d, '--tw-del':s.del }}></div>
              ))}
            </div>
            {/* Near stars — move faster on scroll (closer to viewer) */}
            <div className="absolute inset-0" style={{ transform: `translate3d(0, ${scrollY * 0.08}px, 0)`, willChange: 'transform' }}>
              {starsNear.map((s,i) => (
                <div key={`n${i}`} className="star absolute rounded-full bg-white" style={{ top:`${s.t}%`, left:`${s.l}%`, width:`${s.s}px`, height:`${s.s}px`, '--tw-dur':s.d, '--tw-del':s.del }}></div>
              ))}
            </div>

            {/* ====== VIA LATTEA — slow parallax ====== */}
            <div className="absolute inset-0" style={{ transform: `translate3d(0, ${scrollY * 0.04}px, 0)`, willChange: 'transform' }}>
              <div className="absolute" style={{ top:'-5%', left:'-15%', width:'130%', height:'40%', background:'linear-gradient(135deg, transparent 0%, rgba(147,197,253,0.06) 25%, rgba(196,181,253,0.12) 45%, rgba(255,255,255,0.08) 50%, rgba(196,181,253,0.12) 55%, rgba(147,197,253,0.06) 75%, transparent 100%)', transform:'rotate(-30deg)', borderRadius:'50%', filter:'blur(25px)' }}></div>
              <div className="absolute" style={{ bottom:'0%', right:'-10%', width:'80%', height:'25%', background:'linear-gradient(145deg, transparent 0%, rgba(253,224,71,0.04) 40%, rgba(147,197,253,0.06) 60%, transparent 100%)', transform:'rotate(-20deg)', borderRadius:'50%', filter:'blur(18px)' }}></div>
            </div>

            {/* ====== ORBITE con label astronauta ====== */}
            <div className="absolute rounded-full border border-dashed border-white/[0.1]" style={{ top:'50%', left:'50%', width:'380px', height:'380px', transform:'translate(-50%,-50%) rotateX(55deg)', transformStyle:'preserve-3d' }}></div>
            <div className="absolute rounded-full border border-dashed border-white/[0.07]" style={{ top:'50%', left:'50%', width:'470px', height:'470px', transform:'translate(-50%,-50%) rotateX(55deg)', transformStyle:'preserve-3d' }}></div>
            <div className="absolute rounded-full border border-dashed border-white/[0.04]" style={{ top:'50%', left:'50%', width:'540px', height:'540px', transform:'translate(-50%,-50%) rotateX(55deg)', transformStyle:'preserve-3d' }}></div>

            {/* ====== LABEL ASTRONAUTA ====== */}
            {/* Orbit distance markers */}
            <div className="absolute" style={{ top:'8%', right:'12%' }}>
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-[#4285F4] blink"></div>
                <span className="text-[9px] text-[#4285F4]/60 font-mono tracking-wider">DST 0.42 AU</span>
              </div>
            </div>
            <div className="absolute" style={{ bottom:'12%', left:'8%' }}>
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-[#34A853] blink" style={{ animationDelay:'0.5s' }}></div>
                <span className="text-[9px] text-[#34A853]/60 font-mono tracking-wider">SIG 99.8%</span>
              </div>
            </div>
            <div className="absolute" style={{ top:'50%', right:'2%', transform:'translateY(-50%)' }}>
              <span className="text-[8px] text-white/20 font-mono tracking-widest" style={{ writingMode:'vertical-rl' }}>ORBIT-EXT · 470km</span>
            </div>
            <div className="absolute" style={{ top:'50%', left:'2%', transform:'translateY(-50%)' }}>
              <span className="text-[8px] text-white/20 font-mono tracking-widest" style={{ writingMode:'vertical-rl' }}>ORBIT-INT · 380km</span>
            </div>
            <div className="absolute" style={{ bottom:'5%', right:'30%' }}>
              <span className="text-[8px] text-[#EA4335]/40 font-mono">LAT 43.42°N · LON 11.12°E</span>
            </div>
            <div className="absolute" style={{ top:'3%', left:'25%' }}>
              <span className="text-[8px] text-white/25 font-mono">v 28.4 km/s · T -210°C</span>
            </div>
            {/* Crosshair on planet */}
            <div className="absolute z-20" style={{ top:'50%', left:'50%', transform:'translate(-50%,-50%)', width:'260px', height:'260px', pointerEvents:'none' }}>
              <div className="absolute top-0 left-1/2 w-px h-3 bg-white/10 -translate-x-1/2"></div>
              <div className="absolute bottom-0 left-1/2 w-px h-3 bg-white/10 -translate-x-1/2"></div>
              <div className="absolute top-1/2 left-0 h-px w-3 bg-white/10 -translate-y-1/2"></div>
              <div className="absolute top-1/2 right-0 h-px w-3 bg-white/10 -translate-y-1/2"></div>
            </div>
            {/* Live indicator */}
            <div className="absolute flex items-center gap-1.5" style={{ top:'18%', left:'12%' }}>
              <div className="w-2 h-2 rounded-full bg-[#EA4335] blink"></div>
              <span className="text-[9px] text-[#EA4335]/70 font-mono font-bold tracking-wider">LIVE</span>
            </div>

            {/* ====== PIANETA SCHEDABUSINESS — 3D MARBLE SPHERE ====== */}
            <div className="absolute left-1/2 top-1/2 z-10" style={{ transform: 'translate(-50%,-50%)', width: '240px', height: '240px' }}>
              {/* Atmosphere glow — rainbow halo */}
              <div className="absolute -inset-10 rounded-full" style={{ background:'radial-gradient(circle, rgba(66,133,244,0.12) 0%, rgba(234,67,53,0.06) 25%, rgba(52,168,83,0.04) 40%, transparent 55%)', filter:'blur(18px)', animation:'nebula-drift 8s ease-in-out infinite' }}></div>
              {/* Contact shadow below planet */}
              <div className="absolute" style={{ bottom:'-20px', left:'15%', right:'15%', height:'20px', background:'radial-gradient(ellipse, rgba(0,0,0,0.6) 0%, transparent 70%)', filter:'blur(8px)' }}></div>
              {/* Planet body — 3D GLASS MARBLE */}
              <div className="relative w-full h-full rounded-full overflow-hidden" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.8), 0 0 80px rgba(0,0,0,0.4), inset -30px -20px 50px rgba(0,0,0,0.7), inset 15px 10px 35px rgba(255,255,255,0.08), 0 0 30px rgba(66,133,244,0.08)' }} data-testid="planet-jupiter">
                {/* Base: total black */}
                <div className="absolute inset-0 bg-black"></div>
                {/* === LIGHTER CRUST at edges (planet limb) === */}
                <div className="absolute inset-0 rounded-full" style={{ background:'radial-gradient(circle at 50% 50%, transparent 48%, rgba(60,65,85,0.25) 58%, rgba(85,90,115,0.35) 68%, rgba(110,115,140,0.35) 78%, rgba(140,145,170,0.28) 86%, rgba(160,165,190,0.2) 92%, rgba(180,185,210,0.12) 100%)' }}></div>
                {/* === CRATERS on the surface === */}
                <div className="absolute rounded-full" style={{ top:'10%', left:'6%', width:'32px', height:'24px', background:'radial-gradient(ellipse, rgba(80,85,100,0.35) 0%, rgba(60,65,80,0.15) 50%, transparent 80%)', filter:'blur(2px)' }}></div>
                <div className="absolute rounded-full" style={{ top:'6%', right:'16%', width:'22px', height:'18px', background:'radial-gradient(ellipse, rgba(75,80,95,0.3) 0%, transparent 70%)', filter:'blur(1.5px)' }}></div>
                <div className="absolute rounded-full" style={{ bottom:'12%', left:'14%', width:'26px', height:'20px', background:'radial-gradient(ellipse, rgba(70,75,90,0.3) 0%, transparent 70%)', filter:'blur(2px)' }}></div>
                <div className="absolute rounded-full" style={{ bottom:'8%', right:'8%', width:'20px', height:'16px', background:'radial-gradient(ellipse, rgba(65,70,85,0.25) 0%, transparent 70%)', filter:'blur(1.5px)' }}></div>
                <div className="absolute rounded-full" style={{ top:'38%', left:'3%', width:'18px', height:'14px', background:'radial-gradient(ellipse, rgba(80,85,100,0.2) 0%, transparent 70%)', filter:'blur(1px)' }}></div>
                <div className="absolute rounded-full" style={{ top:'45%', right:'4%', width:'15px', height:'12px', background:'radial-gradient(ellipse, rgba(75,80,95,0.2) 0%, transparent 70%)', filter:'blur(1px)' }}></div>
                {/* === NEBULAE (colored gas clouds) — brighter at edges === */}
                <div className="absolute nebula rounded-full" style={{ top:'6%', left:'8%', width:'55px', height:'38px', background:'radial-gradient(ellipse, rgba(66,133,244,0.45) 0%, rgba(66,133,244,0.12) 50%, transparent 80%)', filter:'blur(8px)' }}></div>
                <div className="absolute nebula rounded-full" style={{ top:'10%', right:'12%', width:'45px', height:'28px', background:'radial-gradient(ellipse, rgba(234,67,53,0.38) 0%, rgba(234,67,53,0.1) 50%, transparent 80%)', filter:'blur(6px)', animationDelay:'2s' }}></div>
                <div className="absolute nebula rounded-full" style={{ bottom:'12%', left:'10%', width:'50px', height:'32px', background:'radial-gradient(ellipse, rgba(251,188,4,0.38) 0%, rgba(251,188,4,0.1) 50%, transparent 80%)', filter:'blur(7px)', animationDelay:'4s' }}></div>
                <div className="absolute nebula rounded-full" style={{ bottom:'8%', right:'6%', width:'40px', height:'28px', background:'radial-gradient(ellipse, rgba(52,168,83,0.38) 0%, rgba(52,168,83,0.1) 50%, transparent 80%)', filter:'blur(6px)', animationDelay:'6s' }}></div>
                <div className="absolute nebula rounded-full" style={{ top:'38%', left:'4%', width:'28px', height:'22px', background:'radial-gradient(ellipse, rgba(168,85,247,0.25) 0%, transparent 70%)', filter:'blur(5px)', animationDelay:'3s' }}></div>
                <div className="absolute nebula rounded-full" style={{ top:'33%', right:'4%', width:'32px', height:'22px', background:'radial-gradient(ellipse, rgba(255,107,53,0.3) 0%, transparent 70%)', filter:'blur(5px)', animationDelay:'5s' }}></div>
                {/* === SPECULAR HIGHLIGHT — bright marble shine top-left === */}
                <div className="absolute inset-0 rounded-full" style={{ background:'radial-gradient(circle at 28% 22%, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0.12) 12%, rgba(255,255,255,0.03) 25%, transparent 45%)', zIndex:12 }}></div>
                {/* === GLASS REFLECTION — curved highlight bar === */}
                <div className="absolute rounded-full" style={{ top:'4%', left:'16%', width:'40%', height:'18%', background:'linear-gradient(180deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.05) 50%, transparent 100%)', filter:'blur(4px)', transform:'rotate(-12deg)', zIndex:13, pointerEvents:'none' }}></div>
                {/* === RIM LIGHT — top and left edge === */}
                <div className="absolute inset-0 rounded-full" style={{ background:'radial-gradient(ellipse at 50% 0%, rgba(255,255,255,0.14) 0%, transparent 28%), radial-gradient(ellipse at 0% 40%, rgba(255,255,255,0.08) 0%, transparent 22%)', zIndex:11 }}></div>
                {/* === DEEP SHADOW — bottom-right terminator === */}
                <div className="absolute inset-0 rounded-full" style={{ background:'radial-gradient(circle at 80% 80%, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.3) 30%, transparent 50%)', zIndex:11 }}></div>
                {/* Logo on BLACK center — nero sotto il font */}
                <div className="absolute inset-0 flex items-center justify-center" style={{ zIndex:14 }}>
                  <div className="px-3 py-1 rounded-full" style={{ background:'radial-gradient(ellipse, rgba(0,0,0,0.98) 0%, rgba(0,0,0,0.9) 50%, transparent 100%)' }}>
                    <span className="text-lg font-black tracking-tight whitespace-nowrap uniposca-stroke" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      <span className="text-[#4285F4]">sch</span><span className="text-[#EA4335]">ed</span><span className="text-[#FBBC04]">abu</span><span className="text-[#4285F4]">sin</span><span className="text-[#34A853]">ess</span><span className="text-[#EA4335]">.com</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* ====== LUNE INTERNE (orbita veloce) — 3D MARBLES ====== */}
            <div className="absolute orbit-ring-fast" style={{ top:'50%', left:'50%', width:'380px', height:'380px', marginTop:'-190px', marginLeft:'-190px', pointerEvents:'none', transform:'rotateX(55deg)', transformStyle:'preserve-3d' }}>
              {/* Luna 1 — Riccardo (foto) — MARBLE */}
              <div className="absolute orbit-item-fast" style={{ top:'-24px', left:'50%', marginLeft:'-24px' }}>
                <div className="marble-moon w-12 h-12 rounded-full overflow-hidden border-2 border-white/40" style={{ position:'relative' }}>
                  <img src="https://customer-assets.emergentagent.com/job_riccardo-sos-google/artifacts/4eu97hbx_Test%20ico%20TNX%20Ricca.png" alt="Riccardo Rossi" className="w-full h-full object-cover" style={{ objectPosition: 'center 25%' }} />
                </div>
              </div>
              {/* Luna 2 — Puzzle — MARBLE */}
              <div className="absolute orbit-item-fast" style={{ bottom:'-20px', left:'50%', marginLeft:'-20px' }}>
                <div className="marble-moon w-10 h-10 rounded-full overflow-hidden border-2 border-white/30" style={{ position:'relative' }} data-testid="puzzle-mini">
                  <img src="https://static.prod-images.emergentagent.com/jobs/9aee3f9a-9ba9-47c5-9ef1-87f76cda4250/images/fb7afdb376edc9f259896097cd88bc221876c0a7b7d59bd610713c909a009c00.png" alt="Il tassello mancante" className="w-full h-full object-cover" />
                </div>
              </div>
              {/* Luna 3 — 100% Deducibile (badge, not marble) */}
              <div className="absolute orbit-item-fast" style={{ top:'50%', right:'-50px', marginTop:'-14px', zIndex:50, pointerEvents:'auto' }}>
                <div className="bg-[#4285F4] rounded-full px-3 py-1.5 shadow-xl border border-[#93c5fd]/30 whitespace-nowrap cursor-pointer hover:bg-[#3574d4] transition-colors"
                  style={{ boxShadow:'0 4px 18px rgba(66,133,244,0.4), inset -3px -3px 8px rgba(0,0,0,0.2), inset 2px 2px 6px rgba(255,255,255,0.15)' }}
                  onClick={() => { window.open('https://www.schedabusiness.it/#deducibile', '_blank'); }} data-testid="deducibile-satellite">
                  <span className="text-white font-black text-[10px]">100% Deducibile</span>
                </div>
              </div>
            </div>

            {/* ====== LUNE ESTERNE (orbita lenta) — 3D MARBLES ====== */}
            <div className="absolute orbit-ring" style={{ top:'50%', left:'50%', width:'470px', height:'470px', marginTop:'-235px', marginLeft:'-235px', pointerEvents:'none' }}>
              {/* Luna — Street View — MARBLE */}
              <div className="absolute orbit-item" style={{ top:'-20px', left:'50%', marginLeft:'-20px' }}>
                <div className="marble-moon w-10 h-10 bg-black rounded-full overflow-hidden border border-white/30" style={{ position:'relative' }}>
                  <img src="https://customer-assets.emergentagent.com/job_sos-activity-cards/artifacts/wr288sey_Google-Street-View-Trusted-Badge-2021.png" alt="Google Street View Trusted" className="w-full h-full object-contain p-1" />
                </div>
              </div>
              {/* Luna — Fotografo — MARBLE */}
              <div className="absolute orbit-item" style={{ top:'50%', right:'-20px', marginTop:'-20px' }}>
                <div className="marble-moon w-10 h-10 bg-black rounded-full overflow-hidden border border-white/30" style={{ position:'relative' }}>
                  <img src="https://customer-assets.emergentagent.com/job_sos-activity-cards/artifacts/eknv50vh_adesivogoogle2.jpg" alt="Fotografo Google Certificato" className="w-full h-full object-contain p-1" />
                </div>
              </div>
              {/* Luna — GoPro MAX — MARBLE */}
              <div className="absolute orbit-item" style={{ bottom:'-20px', left:'50%', marginLeft:'-20px' }}>
                <div className="marble-moon w-10 h-10 bg-black rounded-full overflow-hidden border border-white/30" style={{ position:'relative' }} data-testid="gopro-max-badge">
                  <img src="https://customer-assets.emergentagent.com/job_9aee3f9a-9ba9-47c5-9ef1-87f76cda4250/artifacts/b4icib11_image.png" alt="GoPro MAX 360°" className="w-full h-full object-contain p-1" />
                </div>
              </div>
              {/* Luna — LinkedIn — MARBLE */}
              <div className="absolute orbit-item" style={{ top:'50%', left:'-20px', marginTop:'-20px' }}>
                <a href="https://www.linkedin.com/in/riccardorossi1984googletnx" target="_blank" rel="noopener noreferrer nofollow" className="marble-moon block w-10 h-10 bg-black rounded-full border border-white/30 cursor-pointer overflow-hidden" style={{ pointerEvents:'auto', position:'relative' }} data-testid="linkedin-premium-badge" onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open('https://www.linkedin.com/in/riccardorossi1984googletnx', '_blank'); }}>
                  <svg className="w-full h-full p-1" viewBox="0 0 48 48" fill="none">
                    <rect width="48" height="48" rx="24" fill="#0A66C2"/>
                    <path d="M14.5 19.5h4v13h-4v-13zm2-6.5a2.3 2.3 0 110 4.6 2.3 2.3 0 010-4.6zm6.5 6.5h3.8v1.8h.1c.5-1 1.8-2.1 3.8-2.1 4 0 4.8 2.7 4.8 6.1v7.2h-4v-6.4c0-1.5 0-3.5-2.1-3.5s-2.4 1.7-2.4 3.4v6.5h-4v-13z" fill="white"/>
                    <circle cx="38" cy="10" r="7" fill="#E7A33E"/>
                    <path d="M35.5 10l1.5 1.5 3-3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* ====== TERRA LONTANA CON AURORA BOREALE ====== */}
            <div className="absolute orbit-ring-earth" style={{ top:'50%', left:'50%', width:'620px', height:'620px', marginTop:'-310px', marginLeft:'-310px', pointerEvents:'none' }}>
              <div className="absolute orbit-item-earth" style={{ top:'8%', left:'6%' }}>
                <div className="relative" style={{ width:'24px', height:'24px' }}>
                  <div className="absolute inset-0 rounded-full" style={{ background:'radial-gradient(circle at 40% 35%, #1e40af 0%, #1e3a5f 40%, #0c4a6e 60%, #134e4a 80%, #0a0a0a 100%)', boxShadow:'0 0 6px rgba(30,64,175,0.3)' }}></div>
                  <div className="absolute rounded-full" style={{ top:'30%', left:'35%', width:'6px', height:'4px', background:'#34A853', opacity:0.5, filter:'blur(1px)' }}></div>
                  <div className="aurora-dot absolute rounded-full" style={{ top:'-3px', left:'20%', width:'10px', height:'3px', background:'linear-gradient(90deg, #34A853, #ff69b4)', filter:'blur(2px)', opacity:0.7 }}></div>
                  <div className="aurora-dot absolute rounded-full" style={{ top:'-1px', right:'15%', width:'7px', height:'2px', background:'linear-gradient(90deg, #ff69b4, #34A853)', filter:'blur(2px)', opacity:0.5, animationDelay:'1.5s' }}></div>
                </div>
              </div>
            </div>

            {/* ====== COSMIC DUST — slight parallax ====== */}
            <div className="absolute inset-0" style={{ transform: `translate3d(0, ${scrollY * 0.06}px, 0)`, willChange: 'transform' }}>
              <div className="absolute rounded-full bg-white/20" style={{ top:'20%', right:'15%', width:'3px', height:'3px', animation:'float-dust 8s ease-in-out infinite' }}></div>
              <div className="absolute rounded-full bg-white/15" style={{ bottom:'25%', left:'18%', width:'2px', height:'2px', animation:'float-dust 12s ease-in-out infinite 2s' }}></div>
              <div className="absolute rounded-full bg-white/15" style={{ top:'70%', right:'10%', width:'2px', height:'2px', animation:'float-dust 10s ease-in-out infinite 4s' }}></div>
            </div>

          </div>
        </div>
      </div>
    </div>
  </section>
  );
};


// ====== ANIMATED SHORT — 8 frame storytelling (auto-play) ======
const AnimatedShortSection = () => (
  <section className="py-10 bg-[#0c1220]" data-testid="animated-short-section">
    <div className="max-w-3xl mx-auto px-4">
      <AnimatedShortComponent />
    </div>
  </section>
);

// ====== SCARSITÀ / URGENZA — disponibilita' limitata ======
const ScarsitaSection = () => (
  <section className="pt-20 pb-10 relative overflow-hidden" data-testid="scarsita-section">
    {/* Foto Colle Val d'Elsa — sfondo per il fotografo certificato */}
    <div className="absolute inset-0">
      <img src={AI_IMG.heroColle} alt="Colle Val d'Elsa — territorio di lavoro" className="w-full h-full" style={{ objectFit: 'cover', objectPosition: 'center 70%' }} />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0e1a]/40 via-[#0a0e1a]/55 to-[#0a0e1a]/80"></div>
    </div>
    <div className="max-w-4xl mx-auto px-4 relative z-10">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 bg-[#EA4335]/15 text-[#EA4335] px-4 py-1.5 rounded-full font-bold text-xs mb-4">
          <Clock className="w-4 h-4" /> DISPONIBILITA' LIMITATA
        </div>
        <h2 className="text-2xl md:text-3xl font-black text-white" style={{ fontFamily: "'Poppins',sans-serif" }}>
          Le richieste superano la disponibilita'
        </h2>
        <p className="text-gray-400 mt-2 max-w-xl mx-auto text-sm">
          Sono un professionista singolo, non un call center. Per garantire qualita', limito i clienti seguiti ogni mese.
        </p>
      </div>
      {/* Stats con cornici colorate */}
      <div className="grid grid-cols-3 gap-4 md:gap-6 mb-8">
        <div className="relative overflow-hidden rounded-xl p-[1.5px]">
          <div className="absolute inset-0 bg-gradient-to-b from-[#EA4335] to-[#EA4335]/20 opacity-60"></div>
          <div className="relative bg-[#0a0e1a] rounded-[10px] p-5 text-center">
            <Users className="w-6 h-6 text-[#EA4335] mx-auto mb-2" />
            <div className="text-3xl md:text-4xl font-black text-white">23</div>
            <div className="text-gray-500 text-xs mt-1">richieste / mese</div>
          </div>
        </div>
        <div className="relative overflow-hidden rounded-xl p-[1.5px]">
          <div className="absolute inset-0 bg-gradient-to-b from-[#FBBC04] to-[#FBBC04]/20 opacity-60"></div>
          <div className="relative bg-[#0a0e1a] rounded-[10px] p-5 text-center">
            <Award className="w-6 h-6 text-[#FBBC04] mx-auto mb-2" />
            <div className="text-3xl md:text-4xl font-black text-white">7</div>
            <div className="text-gray-500 text-xs mt-1">posti disponibili</div>
          </div>
        </div>
        <div className="relative overflow-hidden rounded-xl p-[1.5px]">
          <div className="absolute inset-0 bg-gradient-to-b from-[#34A853] to-[#34A853]/20 opacity-60"></div>
          <div className="relative bg-[#0a0e1a] rounded-[10px] p-5 text-center">
            <img src="https://customer-assets.emergentagent.com/job_sos-activity-cards/artifacts/wr288sey_Google-Street-View-Trusted-Badge-2021.png" alt="Google Street View Certificato" className="w-8 h-8 mx-auto mb-2 object-contain" />
            <div className="text-3xl md:text-4xl font-black text-white">1</div>
            <div className="text-gray-500 text-xs mt-1">fotografo certificato</div>
          </div>
        </div>
      </div>
      {/* Animated Short — inline storytelling */}
      <div className="max-w-3xl mx-auto mb-8">
        <AnimatedShortComponent compact />
      </div>
      {/* CTA */}
      <div className="text-center">
        <a
          href="https://wa.me/393420380578?text=Ciao%20Riccardo!%20Vorrei%20prenotare%20una%20consulenza"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-[#4285F4] hover:bg-[#3574d4] text-white rounded-full px-8 py-3.5 font-black text-sm transition-all hover:scale-105"
          style={{ boxShadow: '0 4px 20px rgba(66,133,244,0.3)' }}
          data-testid="prenota-consulenza-btn"
        >
          <Calendar className="w-5 h-5" /> Prenota la tua consulenza
        </a>
      </div>
    </div>
  </section>
);


// The 4 Doors - Photo in Center, Cards Around
const FourDoorsSection = () => {
  const doors = [
    {
      path: "/start",
      title: "Start",
      price: "€99",
      monthly: "€8,25/mese x12",
      paypal3: "€33 x3",
      tagline: "#DignitàDigitale",
      desc: "Verifica, claim e correzione della TUA scheda. Il primo passo per essere visibile.",
      color: "from-[#EA4335] to-[#c5362b]",
      hoverColor: "hover:from-[#f05545] hover:to-[#EA4335]",
      borderColor: "border-[#EA4335]/30",
      customIcon: IconStart,
      features: ["Verifica e claim scheda", "Correzione info base", "Report iniziale"],
      aiImage: AI_IMG.start,
    },
    {
      path: "/plus",
      title: "Plus",
      price: "€299",
      originalPrice: "€399",
      monthly: "€24,91/mese x12",
      paypal3: "€99,66 x3",
      tagline: "Solo per i primi 10",
      desc: "Sospensione? Recensioni? Risolvo io. Il pacchetto per chi ha bisogno di assistenza concreta.",
      color: "from-[#FBBC04] to-[#e0a800]",
      hoverColor: "hover:from-[#fcc934] hover:to-[#FBBC04]",
      borderColor: "border-[#FBBC04]",
      textDark: true,
      customIcon: IconPlus,
      features: ["Recupero sospensione", "Risposta recensioni", "Consulenza strategica"],
      badge: "IL PIU VENDUTO",
      aiImage: AI_IMG.plus,
      featured: true,
    },
    {
      path: "/rooftop",
      title: "Rooftop",
      price: "€599",
      monthly: "€49,92/mese x12",
      paypal3: "€199,67 x3",
      tagline: "TUTTO incluso + assistenza premium",
      desc: "Sali in cima. La soluzione per chi vuole DOMINARE la ricerca locale. Tutto incluso + assistenza premium.",
      color: "from-[#34A853] to-[#2a8f46]",
      hoverColor: "hover:from-[#43b962] hover:to-[#34A853]",
      borderColor: "border-[#34A853]/30",
      customIcon: IconRooftop,
      features: ["Tutto incluso", "Assistenza premium 12 mesi", "Supporto prioritario"],
      badge: "TUTTO INCLUSO",
      aiImage: AI_IMG.rooftop,
    },
    {
      path: "/deducibile100",
      title: "Virtual Tour",
      price: "€499",
      originalPrice: "€699",
      monthly: "€41,58/mese x12",
      paypal3: "€166,33 x3",
      tagline: "SOLO IN FASE DI LANCIO",
      desc: "Tour immersivo e completo. La TUA attivita' visibile a 360° su Google Maps. Timewarp IN REGALO.",
      color: "from-[#0a0a0a] to-[#0a0a0a]",
      hoverColor: "hover:from-[#111] hover:to-[#0a0a0a]",
      borderColor: "border-transparent",
      useImage: FOTOGRAFO_IMG,
      features: ["GoPro MAX 360°", "Start INCLUSO", "Timewarp IN REGALO"],
      badge: "LANCIO",
      typewriter: true,
      aiImage: AI_IMG.virtualTour,
      isGoldLuxury: true,
      gradientBorder: true,
    },
  ];

  // 5th door - Deducibile 100% (full width below the grid)
  const formazione = {
    path: "https://www.schedabusiness.it",
    title: "Deducibile 100%",
    price: "",
    paypal3: null,
    tagline: "TAEG 0% · 3 rate PayPal",
    desc: "Formazione professionale 100% deducibile per P.IVA e imprese. Paghi ora, tra 12 mesi porti in detrazione fiscale = non paghi il corso e sei formato!",
    features: ["100% deducibile", "TAEG 0%", "3 rate PayPal"],
  };

  return (
    <section id="pacchetti" className="py-16 bg-[#0c1220]" data-testid="four-doors-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 font-google">
            Inquadra il <span className="text-[#ff6b35]">problema</span>, trova la soluzione
          </h2>
          <p className="text-gray-400 text-base md:text-lg">Soluzioni su misura per la TUA attivita'</p>
        </div>

        {/* Academy - 5th Door (top, full width) */}
        <div className="mb-6">
          <a
            href={formazione.path}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative bg-gradient-to-r from-[#1a3a6b] via-[#2557a7] to-[#4285F4] rounded-3xl p-5 md:p-7 transition-all duration-300 hover:scale-[1.01] hover:shadow-2xl border border-[#4285F4]/30 overflow-hidden block"
            data-testid="door-formazione"
          >
            <div className="absolute top-3 right-3 bg-black/80 backdrop-blur-sm text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-2 border border-white/20">
              <svg className="w-6 h-5" viewBox="0 0 24 24" fill="none"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.771.771 0 0 1 .761-.652h6.199c2.063 0 3.543.585 4.268 1.694.335.516.538 1.087.617 1.742.083.677.032 1.478-.157 2.398l-.013.063v.564l.44.249c.372.21.67.455.893.735.37.464.607 1.042.7 1.715.096.693.037 1.498-.173 2.393-.241 1.028-.635 1.922-1.171 2.655a5.292 5.292 0 0 1-1.539 1.546c-.593.39-1.298.672-2.097.84-.769.161-1.64.245-2.582.245h-.614a1.793 1.793 0 0 0-1.77 1.513l-.046.244-.77 4.88-.036.174a.164.164 0 0 1-.047.1.13.13 0 0 1-.078.028z" fill="#253B80"/><path d="M18.36 7.667a7.37 7.37 0 0 0-.078.449c-1 5.155-4.426 6.933-8.801 6.933h-2.228a1.08 1.08 0 0 0-1.07.918L5.09 22.83l-.323 2.054a.57.57 0 0 0 .563.66h3.952a.95.95 0 0 0 .94-.803l.038-.201.744-4.728.048-.26a.951.951 0 0 1 .94-.804h.591c3.83 0 6.828-1.556 7.704-6.063.366-1.882.176-3.453-.793-4.558a3.793 3.793 0 0 0-1.082-.835z" fill="#179BD7"/></svg>
              3 RATE · TAEG 0%
            </div>
            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
              <div className="w-12 h-12 md:w-14 md:h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl md:text-2xl font-bold text-white mb-0.5">{formazione.title}</h3>
                <p className="text-xs text-white/70 mb-1">{formazione.tagline}</p>
                <p className="text-xs text-white/80 leading-relaxed hidden md:block">{formazione.desc}</p>
              </div>
              <div className="flex items-center gap-4 md:gap-6">
                <div className="flex items-center gap-2 text-sm font-bold text-white group-hover:translate-x-2 transition-transform">
                  Scopri <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </div>
            <div className="absolute -bottom-8 -right-8 w-28 h-28 bg-white/5 rounded-full"></div>
          </a>
        </div>

        {/* Desktop: 4 roads with photo roundabout at center */}
        {/* Mobile: Academy → Photo → Start → Plus → Rooftop → VT360 */}
        <div className="relative">
          {/* Center Photo Roundabout - Desktop */}
          <div className="hidden lg:flex absolute inset-0 items-center justify-center z-10 pointer-events-none" style={{ top: 0, bottom: 0 }}>
            <div className="w-44 h-44 bg-[#0c1220] rounded-full shadow-2xl border-[5px] border-[#0c1220] overflow-hidden flex items-end justify-center relative" style={{ boxShadow: '0 0 0 4px rgba(255,107,53,0.2), 0 25px 50px -12px rgba(0,0,0,0.5)' }}>
              <img
                src="https://customer-assets.emergentagent.com/job_riccardo-sos-google/artifacts/4eu97hbx_Test%20ico%20TNX%20Ricca.png"
                alt="Riccardo Rossi"
                className="w-full h-auto object-cover absolute bottom-0"
                style={{ clipPath: 'inset(35% 0 0 0)', transform: 'translateY(-15%)' }}
              />
              <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-[#ff6b35] text-white px-3 py-1 rounded-full text-[10px] font-bold whitespace-nowrap shadow-lg z-20">
                Riccardo Rossi
              </div>
            </div>
          </div>

          {/* Plus Glow Animation */}
          <style>{`
            @keyframes plus-glow {
              0%, 100% { box-shadow: 0 0 15px rgba(251,188,4,0.4), 0 0 30px rgba(251,188,4,0.2), 0 0 60px rgba(251,188,4,0.1); }
              50% { box-shadow: 0 0 25px rgba(251,188,4,0.6), 0 0 50px rgba(251,188,4,0.35), 0 0 80px rgba(251,188,4,0.15); }
            }
            @keyframes badge-shine {
              0% { background-position: -200% center; }
              100% { background-position: 200% center; }
            }
            @keyframes talpa-wave {
              0% { left: -30%; }
              100% { left: 110%; }
            }
            @keyframes talpa-bump {
              0%, 15%, 85%, 100% { transform: translateY(0) scaleY(1); }
              40%, 60% { transform: translateY(-4px) scaleY(1.1); }
            }
            .plus-card-glow {
              animation: plus-glow 2s ease-in-out infinite;
              border: 2px solid #FBBC04 !important;
              transform: scale(1.03);
              z-index: 5;
            }
            .plus-card-glow:hover {
              transform: scale(1.05) !important;
            }
            .white-glow-card {
              animation: white-glow 2s ease-in-out infinite;
              border: 2px solid rgba(255,255,255,0.6) !important;
            }
            @keyframes white-glow {
              0%, 100% { box-shadow: 0 0 15px rgba(255,255,255,0.3), 0 0 30px rgba(255,255,255,0.15); }
              50% { box-shadow: 0 0 25px rgba(255,255,255,0.5), 0 0 50px rgba(255,255,255,0.25); }
            }
            .plus-badge-shine {
              background: linear-gradient(90deg, rgba(251,188,4,0.15) 0%, rgba(251,188,4,0.15) 40%, rgba(251,188,4,0.5) 50%, rgba(251,188,4,0.15) 60%, rgba(251,188,4,0.15) 100%);
              background-size: 200% 100%;
              animation: badge-shine 3s ease-in-out infinite;
            }
            .talpa-badge {
              position: relative;
              overflow: hidden;
              animation: talpa-bump 2.5s ease-in-out infinite;
              background: linear-gradient(135deg, rgba(251,188,4,0.25) 0%, rgba(251,188,4,0.1) 100%);
            }
            .talpa-badge::before {
              content: '';
              position: absolute;
              top: 0;
              left: -30%;
              width: 30%;
              height: 100%;
              background: radial-gradient(ellipse at center, rgba(255,255,255,0.55) 0%, rgba(251,188,4,0.45) 40%, transparent 70%);
              border-radius: 50%;
              animation: talpa-wave 2.5s ease-in-out infinite;
              filter: blur(3px);
              z-index: 1;
            }
            .talpa-badge::after {
              content: '';
              position: absolute;
              bottom: -2px;
              left: -30%;
              width: 25%;
              height: 6px;
              background: radial-gradient(ellipse at center, rgba(0,0,0,0.2) 0%, transparent 70%);
              border-radius: 50%;
              animation: talpa-wave 2.5s ease-in-out infinite;
              z-index: 0;
            }
          `}</style>

          {/* Desktop Grid: 2x2 with gap for roundabout */}
          <div className="hidden lg:grid grid-cols-2 gap-5">
            {doors.map((door) => {
              const cardContent = (
                <>
                  {/* AI-generated image as subtle background */}
                  {door.aiImage && (
                    <div className="absolute inset-0 opacity-[0.12] group-hover:opacity-[0.18] transition-opacity duration-500">
                      <img src={door.aiImage} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  {door.badge && !door.featured ? (
                    <div className={`absolute top-3 right-3 ${door.isGoldLuxury ? 'bg-[#D4AF37]/20 text-[#F5E6A3]' : 'bg-black/80 text-white'} backdrop-blur-sm text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 border ${door.isGoldLuxury ? 'border-[#D4AF37]/30' : 'border-white/20'}`}>
                      <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.771.771 0 0 1 .761-.652h6.199c2.063 0 3.543.585 4.268 1.694.335.516.538 1.087.617 1.742.083.677.032 1.478-.157 2.398l-.013.063v.564l.44.249c.372.21.67.455.893.735.37.464.607 1.042.7 1.715.096.693.037 1.498-.173 2.393-.241 1.028-.635 1.922-1.171 2.655a5.292 5.292 0 0 1-1.539 1.546c-.593.39-1.298.672-2.097.84-.769.161-1.64.245-2.582.245h-.614a1.793 1.793 0 0 0-1.77 1.513l-.046.244-.77 4.88-.036.174a.164.164 0 0 1-.047.1.13.13 0 0 1-.078.028z" fill="#253B80"/><path d="M18.36 7.667a7.37 7.37 0 0 0-.078.449c-1 5.155-4.426 6.933-8.801 6.933h-2.228a1.08 1.08 0 0 0-1.07.918L5.09 22.83l-.323 2.054a.57.57 0 0 0 .563.66h3.952a.95.95 0 0 0 .94-.803l.038-.201.744-4.728.048-.26a.951.951 0 0 1 .94-.804h.591c3.83 0 6.828-1.556 7.704-6.063.366-1.882.176-3.453-.793-4.558a3.793 3.793 0 0 0-1.082-.835z" fill="#179BD7"/></svg>
                      {door.badge}
                    </div>
                  ) : null}
                  {door.useImage ? (
                    <div className={`w-14 h-14 ${door.isGoldLuxury ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/30' : 'bg-white/20'} backdrop-blur-sm rounded-2xl flex items-center justify-center mb-3 overflow-hidden`}>
                      <img src={door.useImage} alt={door.title} className="w-12 h-12 object-contain" />
                    </div>
                  ) : (
                    <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-3">
                      <door.customIcon className={`w-7 h-7 ${door.textDark ? 'text-gray-900' : 'text-white'}`} />
                    </div>
                  )}
                  <h3 className={`text-xl md:text-2xl font-black italic mb-0.5 ${door.typewriter ? 'font-typewriter' : ''}`}
                    style={door.isGoldLuxury ? { fontFamily: "'Poppins', sans-serif", background: 'linear-gradient(135deg, #D4AF37, #F5E6A3, #D4AF37, #B8942E)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' } : { color: door.textDark ? '#111' : '#fff' }}>
                    {door.title}
                  </h3>
                  <p className={`text-xs mb-2 ${door.isGoldLuxury ? 'text-[#D4AF37]/70' : door.textDark ? 'text-gray-800/70' : 'text-white/70'} ${door.typewriter ? 'font-typewriter' : ''}`}>
                    {door.tagline}
                  </p>
                  <div className="flex items-baseline gap-2 mb-1">
                    {door.originalPrice && (
                      <span className={`text-lg font-black ${door.isGoldLuxury ? 'text-gray-500' : 'text-gray-900/50'} line-through`}>{door.originalPrice}</span>
                    )}
                    <span className={`text-2xl md:text-3xl font-black`}
                      style={door.isGoldLuxury ? { background: 'linear-gradient(135deg, #D4AF37, #F5E6A3, #D4AF37)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' } : { color: door.textDark ? '#111' : '#fff' }}>
                      {door.price}
                    </span>
                  </div>
                  <div className={`flex items-center gap-1.5 mb-1 ${door.isGoldLuxury ? 'text-[#D4AF37]/70' : door.textDark ? 'text-gray-900/70' : 'text-white/70'}`}>
                    <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.771.771 0 0 1 .761-.652h6.199c2.063 0 3.543.585 4.268 1.694.335.516.538 1.087.617 1.742.083.677.032 1.478-.157 2.398l-.013.063v.564l.44.249c.372.21.67.455.893.735.37.464.607 1.042.7 1.715.096.693.037 1.498-.173 2.393-.241 1.028-.635 1.922-1.171 2.655a5.292 5.292 0 0 1-1.539 1.546c-.593.39-1.298.672-2.097.84-.769.161-1.64.245-2.582.245h-.614a1.793 1.793 0 0 0-1.77 1.513l-.046.244-.77 4.88-.036.174a.164.164 0 0 1-.047.1.13.13 0 0 1-.078.028z" fill="#253B80"/><path d="M18.36 7.667a7.37 7.37 0 0 0-.078.449c-1 5.155-4.426 6.933-8.801 6.933h-2.228a1.08 1.08 0 0 0-1.07.918L5.09 22.83l-.323 2.054a.57.57 0 0 0 .563.66h3.952a.95.95 0 0 0 .94-.803l.038-.201.744-4.728.048-.26a.951.951 0 0 1 .94-.804h.591c3.83 0 6.828-1.556 7.704-6.063.366-1.882.176-3.453-.793-4.558a3.793 3.793 0 0 0-1.082-.835z" fill="#179BD7"/></svg>
                    <span className="text-xs font-bold">oppure <strong>{door.paypal3}</strong> TAEG 0%</span>
                  </div>
                  {door.featured && door.badge && (
                    <div className="inline-block talpa-badge text-gray-900 text-sm font-black px-5 py-2 rounded-full border border-gray-900/20 mb-2" style={{ zIndex: 2 }}>
                      <span style={{ position: 'relative', zIndex: 2 }}>{door.badge}</span>
                    </div>
                  )}
                  <p className={`text-xs mb-3 leading-relaxed ${door.isGoldLuxury ? 'text-gray-400' : door.textDark ? 'text-gray-800/80' : 'text-white/80'}`}>
                    {door.desc}
                  </p>
                  <ul className="space-y-1 mb-3">
                    {door.features.map((f, i) => (
                      <li key={i} className={`flex items-center gap-1.5 text-xs ${door.isGoldLuxury ? 'text-gray-300' : door.textDark ? 'text-gray-900' : 'text-white'}`}>
                        <CheckCircle className={`w-3.5 h-3.5 flex-shrink-0 ${door.isGoldLuxury ? 'text-[#D4AF37]/60' : door.textDark ? 'text-gray-900/60' : 'text-white/60'}`} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  {door.featured ? (
                    <div className="inline-flex items-center gap-2 bg-gray-900 text-[#FBBC04] text-sm font-black px-5 py-2.5 rounded-full group-hover:translate-x-2 transition-transform shadow-lg">
                      Scopri di più <ArrowRight className="w-4 h-4" />
                    </div>
                  ) : door.isGoldLuxury ? (
                    <div className="inline-flex items-center gap-2 text-sm font-black px-5 py-2.5 rounded-full group-hover:translate-x-2 transition-transform" style={{ background: 'linear-gradient(135deg, #D4AF37, #B8942E)', color: '#000' }}>
                      Scopri di più <ArrowRight className="w-4 h-4" />
                    </div>
                  ) : (
                    <div className={`flex items-center gap-2 text-sm font-bold ${door.textDark ? 'text-gray-900' : 'text-white'} group-hover:translate-x-2 transition-transform`}>
                      Scopri di più <ArrowRight className="w-4 h-4" />
                    </div>
                  )}
                </>
              );

              if (door.gradientBorder) {
                return (
                  <Link
                    key={door.path}
                    to={door.path}
                    className="group relative rounded-3xl p-[2px] transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl overflow-hidden"
                    data-testid={`door-${door.path.slice(1)}`}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-[#4285F4] via-[#EA4335] via-[#FBBC04] to-[#34A853] opacity-70 group-hover:opacity-100 transition-opacity rounded-3xl"></div>
                    <div className="relative bg-[#0a0a0a] rounded-[22px] p-5 md:p-7 overflow-hidden">
                      {cardContent}
                    </div>
                  </Link>
                );
              }

              return (
              <Link
                key={door.path}
                to={door.path}
                className={`group relative bg-gradient-to-br ${door.color} ${door.hoverColor} rounded-3xl p-5 md:p-7 transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl border ${door.borderColor} overflow-hidden ${door.featured ? 'plus-card-glow' : ''}`}
                data-testid={`door-${door.path.slice(1)}`}
              >
                {cardContent}
              </Link>
              );
            })}
          </div>

          {/* Mobile: Stacked with photo between Academy and Start */}
          <div className="lg:hidden space-y-4">
            {/* Mobile Photo - Between Academy and cards */}
            <div className="flex justify-center py-2">
              <div className="w-28 h-28 bg-[#0c1220] rounded-full shadow-xl border-4 border-[#ff6b35]/20 overflow-hidden flex items-end justify-center relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_riccardo-sos-google/artifacts/4eu97hbx_Test%20ico%20TNX%20Ricca.png"
                  alt="Riccardo Rossi"
                  className="w-full h-auto object-cover absolute bottom-0"
                  style={{ clipPath: 'inset(35% 0 0 0)', transform: 'translateY(-15%)' }}
                />
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-[#ff6b35] text-white px-2.5 py-0.5 rounded-full text-[9px] font-bold whitespace-nowrap shadow-lg z-20">
                  Riccardo Rossi
                </div>
              </div>
            </div>
            {/* Mobile cards stacked */}
            {doors.map((door) => {
              const PayPalP = () => (
                <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.771.771 0 0 1 .761-.652h6.199c2.063 0 3.543.585 4.268 1.694.335.516.538 1.087.617 1.742.083.677.032 1.478-.157 2.398l-.013.063v.564l.44.249c.372.21.67.455.893.735.37.464.607 1.042.7 1.715.096.693.037 1.498-.173 2.393-.241 1.028-.635 1.922-1.171 2.655a5.292 5.292 0 0 1-1.539 1.546c-.593.39-1.298.672-2.097.84-.769.161-1.64.245-2.582.245h-.614a1.793 1.793 0 0 0-1.77 1.513l-.046.244-.77 4.88-.036.174a.164.164 0 0 1-.047.1.13.13 0 0 1-.078.028z" fill="#253B80"/><path d="M18.36 7.667a7.37 7.37 0 0 0-.078.449c-1 5.155-4.426 6.933-8.801 6.933h-2.228a1.08 1.08 0 0 0-1.07.918L5.09 22.83l-.323 2.054a.57.57 0 0 0 .563.66h3.952a.95.95 0 0 0 .94-.803l.038-.201.744-4.728.048-.26a.951.951 0 0 1 .94-.804h.591c3.83 0 6.828-1.556 7.704-6.063.366-1.882.176-3.453-.793-4.558a3.793 3.793 0 0 0-1.082-.835z" fill="#179BD7"/></svg>
              );

              const mobileInner = (
                <>
                  {door.badge && !door.featured ? (
                    <div className={`absolute top-3 right-3 ${door.isGoldLuxury ? 'bg-[#D4AF37]/20 text-[#F5E6A3]' : 'bg-black/80 text-white'} backdrop-blur-sm text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 border ${door.isGoldLuxury ? 'border-[#D4AF37]/30' : 'border-white/20'}`}>
                      <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.771.771 0 0 1 .761-.652h6.199c2.063 0 3.543.585 4.268 1.694.335.516.538 1.087.617 1.742.083.677.032 1.478-.157 2.398l-.013.063v.564l.44.249c.372.21.67.455.893.735.37.464.607 1.042.7 1.715.096.693.037 1.498-.173 2.393-.241 1.028-.635 1.922-1.171 2.655a5.292 5.292 0 0 1-1.539 1.546c-.593.39-1.298.672-2.097.84-.769.161-1.64.245-2.582.245h-.614a1.793 1.793 0 0 0-1.77 1.513l-.046.244-.77 4.88-.036.174a.164.164 0 0 1-.047.1.13.13 0 0 1-.078.028z" fill="#253B80"/><path d="M18.36 7.667a7.37 7.37 0 0 0-.078.449c-1 5.155-4.426 6.933-8.801 6.933h-2.228a1.08 1.08 0 0 0-1.07.918L5.09 22.83l-.323 2.054a.57.57 0 0 0 .563.66h3.952a.95.95 0 0 0 .94-.803l.038-.201.744-4.728.048-.26a.951.951 0 0 1 .94-.804h.591c3.83 0 6.828-1.556 7.704-6.063.366-1.882.176-3.453-.793-4.558a3.793 3.793 0 0 0-1.082-.835z" fill="#179BD7"/></svg>
                      {door.badge}
                    </div>
                  ) : null}
                  <div className="flex items-start gap-4">
                    {door.useImage ? (
                      <div className={`w-14 h-14 ${door.isGoldLuxury ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/30' : 'bg-white/20'} backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden`}>
                        <img src={door.useImage} alt={door.title} className="w-12 h-12 object-contain" />
                      </div>
                    ) : (
                      <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0">
                        <door.customIcon className={`w-7 h-7 ${door.textDark ? 'text-gray-900' : 'text-white'}`} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className={`text-xl font-black italic ${door.typewriter ? 'font-typewriter' : ''}`}
                        style={door.isGoldLuxury ? { fontFamily: "'Poppins',sans-serif", background: 'linear-gradient(135deg, #D4AF37, #F5E6A3, #D4AF37, #B8942E)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' } : { color: door.textDark ? '#111' : '#fff' }}>
                        {door.title}
                      </h3>
                      <p className={`text-xs mt-0.5 ${door.isGoldLuxury ? 'text-[#D4AF37]/70' : door.textDark ? 'text-gray-800/70' : 'text-white/60'}`}>{door.tagline}</p>
                    </div>
                  </div>
                  {/* Prezzo + PayPal rate */}
                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex items-baseline gap-2">
                      {door.originalPrice && (
                        <span className="text-base font-black text-gray-500 line-through">{door.originalPrice}</span>
                      )}
                      <span className="text-2xl font-black"
                        style={door.isGoldLuxury ? { background: 'linear-gradient(135deg, #D4AF37, #F5E6A3, #D4AF37)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' } : { color: door.textDark ? '#111' : '#fff' }}>
                        {door.price}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 bg-white/[0.06] rounded-lg px-3 py-1.5">
                      <PayPalP />
                      <span className={`text-sm font-black ${door.isGoldLuxury ? 'text-[#F5E6A3]' : door.textDark ? 'text-gray-900' : 'text-white'}`}>{door.paypal3}</span>
                    </div>
                  </div>
                  {door.featured && door.badge && (
                    <div className="mt-2 inline-block talpa-badge text-gray-900 text-sm font-black px-5 py-2 rounded-full border border-gray-900/20">
                      <span style={{ position: 'relative', zIndex: 2 }}>{door.badge}</span>
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {door.features.map((f, i) => (
                      <span key={i} className={`text-xs px-2.5 py-1 rounded-full font-bold ${door.isGoldLuxury ? 'bg-[#D4AF37]/10 text-[#F5E6A3]' : door.textDark ? 'bg-gray-900/10 text-gray-900' : 'bg-white/10 text-white/80'}`}>
                        {f}
                      </span>
                    ))}
                  </div>
                </>
              );

              if (door.gradientBorder) {
                return (
                  <Link key={door.path} to={door.path} className="group relative rounded-2xl p-[2px] transition-all duration-300 hover:shadow-xl overflow-hidden block" data-testid={`door-mobile-${door.path.slice(1)}`}>
                    <div className="absolute inset-0 bg-gradient-to-r from-[#4285F4] via-[#EA4335] via-[#FBBC04] to-[#34A853] opacity-70 rounded-2xl"></div>
                    <div className="relative bg-[#0a0a0a] rounded-[14px] p-5 overflow-hidden">{mobileInner}</div>
                  </Link>
                );
              }

              return (
              <Link
                key={door.path}
                to={door.path}
                className={`group relative bg-gradient-to-br ${door.color} ${door.hoverColor} rounded-2xl p-5 transition-all duration-300 hover:shadow-xl border ${door.borderColor} overflow-hidden block ${door.featured ? 'plus-card-glow' : ''}`}
                data-testid={`door-mobile-${door.path.slice(1)}`}
              >
                {mobileInner}
              </Link>
              );
            })}
          </div>
        </div>

        {/* PayPal Trust Bar */}
        <div className="mt-10 bg-white/5 rounded-2xl p-4 md:p-6 border border-white/10">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8">
            <a href="https://www.paypal.com/it/digital-wallet/ways-to-pay/buy-now-pay-later" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
              <svg className="h-6 w-auto" viewBox="0 0 101 32" fill="#4285F4"><path d="M12.2 4.6h-7c-.5 0-.9.3-1 .8L1.5 24c-.1.4.2.7.6.7h3.4c.5 0 .9-.3 1-.8l.8-5.1c.1-.5.5-.8 1-.8h2.3c4.7 0 7.5-2.3 8.2-6.8.3-2 0-3.5-.9-4.6C16.8 5.3 14.8 4.6 12.2 4.6z"/><path d="M39.6 4.6h-7c-.5 0-.9.3-1 .8l-2.7 18.6c-.1.4.2.7.6.7h3.6c.3 0 .6-.2.7-.6l.8-5.3c.1-.5.5-.8 1-.8h2.3c4.7 0 7.5-2.3 8.2-6.8.3-2 0-3.5-.9-4.6C44.2 5.3 42.2 4.6 39.6 4.6z"/><path d="M67.3 10.5c-1-1.1-3-1.7-5.6-1.7h-7c-.5 0-.9.3-1 .8L51 28.2c-.1.4.2.7.6.7h3.4c.5 0 .9-.4 1-.8l.7-4.6c.1-.5.5-.8 1-.8h2.3c4.7 0 7.5-2.3 8.2-6.8C68.5 13.8 68.3 12.2 67.3 10.5z"/></svg>
              <span className="text-sm font-bold text-[#4285F4] hover:underline">Paga in 3 rate</span>
            </a>
            <span className="text-white/20 hidden md:block">|</span>
            <span className="text-sm text-gray-400"><strong className="text-green-400">TAEG 0%</strong> — Nessun interesse</span>
            <span className="text-white/20 hidden md:block">|</span>
            <span className="text-sm text-gray-400">Pagamento sicuro e protetto</span>
            <span className="text-white/20 hidden md:block">|</span>
            <div className="flex items-center gap-1">
              <Shield className="w-4 h-4 text-[#4285F4]" />
              <span className="text-sm text-gray-400">Protezione acquisti PayPal</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// The Missing Puzzle Piece - Impact Section
const PuzzleSection = () => (
  <section className="py-16 bg-[#0a0e1a] relative overflow-hidden" data-testid="puzzle-section">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-8 items-center">
        {/* Image */}
        <div className="flex justify-center order-2 lg:order-1">
          <img
            src={AI_IMG.puzzle}
            alt="schedabusiness.com — L'anello di congiunzione"
            className="w-full max-w-md rounded-2xl shadow-2xl"
            data-testid="puzzle-image"
          />
        </div>
        {/* Copy */}
        <div className="order-1 lg:order-2 text-center lg:text-left">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-black text-white font-google leading-tight mb-4">
            L'anello di <span className="text-[#ff6b35]">congiunzione</span>
          </h2>
          <p className="text-gray-400 text-sm md:text-base leading-relaxed mb-4">
            Da una parte c'e' il <strong className="text-white">motore di ricerca</strong>, che muove il mondo.
            Dall'altra c'e' la <strong className="text-white">TUA attivita'</strong>: una scheda incompleta, sospesa o invisibile.
          </p>
          <p className="text-gray-200 text-base md:text-lg font-bold leading-relaxed mb-6">
            Nel mezzo ci siamo <span className="text-[#ff6b35]">noi</span>. Il pezzo di puzzle che unisce la TUA attivita' a chi la sta cercando.
          </p>
          {/* 3D Glass Puzzle Pieces — BIGGER, BOLDER, 3D TEXT */}
          <style>{`
            .jig3d-group { perspective: 800px; }
            .jig3d { transform-style: preserve-3d; }
            .jig3d.jl { animation: jl-float 3s ease-in-out infinite; }
            .jig3d.jr { animation: jr-float 3s ease-in-out infinite; }
            .jig3d.jc { animation: jc-pulse 3s ease-in-out infinite; }
            @keyframes jl-float { 0%,100% { transform: rotateX(12deg) rotateY(-3deg) translateX(-16px); } 50% { transform: rotateX(12deg) rotateY(-3deg) translateX(-10px); } }
            @keyframes jr-float { 0%,100% { transform: rotateX(12deg) rotateY(3deg) translateX(16px); } 50% { transform: rotateX(12deg) rotateY(3deg) translateX(10px); } }
            @keyframes jc-pulse { 0%,100% { transform: rotateX(12deg) scale(1); } 50% { transform: rotateX(12deg) scale(1.02); } }
            .jig3d-group:hover .jl { animation: none; transform: rotateX(12deg) rotateY(-3deg) translateX(0); transition: transform 0.5s cubic-bezier(0.22,1,0.36,1); }
            .jig3d-group:hover .jr { animation: none; transform: rotateX(12deg) rotateY(3deg) translateX(0); transition: transform 0.5s cubic-bezier(0.22,1,0.36,1); }
            .jig3d-group:hover .jc { animation: none; transform: rotateX(12deg) scale(1); transition: transform 0.5s cubic-bezier(0.22,1,0.36,1); }
            .puzzle-text-3d {
              -webkit-text-stroke: 1.5px #000;
              paint-order: stroke fill;
              text-shadow: 1px 1px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 2px 2px 0 rgba(0,0,0,0.6), 3px 3px 0 rgba(0,0,0,0.35), 4px 4px 6px rgba(0,0,0,0.3);
            }
            .puzzle-label-3d {
              -webkit-text-stroke: 0.5px rgba(0,0,0,0.6);
              paint-order: stroke fill;
              text-shadow: 1px 1px 0 rgba(0,0,0,0.5), 2px 2px 2px rgba(0,0,0,0.3);
            }
          `}</style>
          <div className="jig3d-group flex items-center justify-center lg:justify-start cursor-pointer" style={{ gap: 0 }}>
            {/* LEFT — Google (notch on right edge) */}
            <div className="jig3d jl relative" style={{ width: 170, height: 100, zIndex: 1 }}>
              <svg viewBox="0 0 130 80" width="170" height="100" style={{ overflow: 'visible', filter: 'drop-shadow(0 10px 18px rgba(66,133,244,0.35))' }}>
                <defs>
                  <linearGradient id="gbl" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#dbeafe"/><stop offset="100%" stopColor="#93c5fd"/></linearGradient>
                  <linearGradient id="ebl" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#60a5fa"/><stop offset="100%" stopColor="#3b82f6"/></linearGradient>
                </defs>
                <path d="M8,6 L130,6 L130,27 C122,27 116,32 116,40 C116,48 122,53 130,53 L130,78 L8,78 Q2,78 2,72 L2,12 Q2,6 8,6 Z" fill="url(#ebl)" transform="translate(1.5,1.5)" opacity="0.4"/>
                <path d="M8,4 L130,4 L130,26 C122,26 116,31 116,39 C116,47 122,52 130,52 L130,76 L8,76 Q2,76 2,70 L2,10 Q2,4 8,4 Z" fill="url(#gbl)" stroke="#60a5fa" strokeWidth="1.5"/>
                <path d="M10,8 L126,8" fill="none" stroke="white" strokeWidth="2" opacity="0.5" strokeLinecap="round"/>
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ paddingRight: 14 }}>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-[#4285F4]" style={{ boxShadow:'0 0 6px rgba(66,133,244,0.6)' }}></span>
                  <span className="text-xl font-black text-white puzzle-text-3d" style={{ fontFamily:"'Poppins',sans-serif" }}>Google</span>
                </div>
                <span className="text-sm text-blue-800 font-extrabold puzzle-label-3d mt-0.5">L'ecosistema</span>
              </div>
            </div>
            {/* CENTER — schedabusiness.com LOGO UFFICIALE */}
            <div className="jig3d jc relative" style={{ width: 240, height: 100, zIndex: 3, marginLeft: -18, marginRight: -18 }}>
              <svg viewBox="0 0 184 80" width="240" height="100" style={{ overflow: 'visible', filter: 'drop-shadow(0 12px 24px rgba(0,0,0,0.5))' }}>
                <defs>
                  <linearGradient id="gdk" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#1e293b"/><stop offset="100%" stopColor="#0c1220"/></linearGradient>
                  <linearGradient id="edk" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0f172a"/><stop offset="100%" stopColor="#020617"/></linearGradient>
                </defs>
                <path d="M14,6 L170,6 L170,27 C178,27 184,32 184,40 C184,48 178,53 170,53 L170,78 L14,78 L14,53 C6,53 0,48 0,40 C0,32 6,27 14,27 Z" fill="url(#edk)" transform="translate(1.5,1.5)" opacity="0.5"/>
                <path d="M14,4 L170,4 L170,26 C178,26 184,31 184,39 C184,47 178,52 170,52 L170,76 L14,76 L14,52 C6,52 0,47 0,39 C0,31 6,26 14,26 Z" fill="url(#gdk)" stroke="#334155" strokeWidth="0.7"/>
                <path d="M18,8 L166,8" fill="none" stroke="white" strokeWidth="1" opacity="0.15" strokeLinecap="round"/>
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-lg font-black whitespace-nowrap puzzle-text-3d" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  <span style={{color:'#4285F4'}}>sch</span><span style={{color:'#EA4335'}}>ed</span><span style={{color:'#FBBC04'}}>abu</span><span style={{color:'#4285F4'}}>sin</span><span style={{color:'#34A853'}}>ess</span><span style={{color:'#EA4335'}}>.com</span>
                </span>
                <span className="text-sm text-white/60 font-extrabold puzzle-label-3d mt-0.5">L'anello</span>
              </div>
            </div>
            {/* RIGHT — La TUA attivita' (notch on left edge) */}
            <div className="jig3d jr relative" style={{ width: 190, height: 100, zIndex: 1 }}>
              <svg viewBox="0 0 155 80" width="190" height="100" style={{ overflow: 'visible', filter: 'drop-shadow(0 10px 18px rgba(34,168,83,0.35))' }}>
                <defs>
                  <linearGradient id="ggn" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#dcfce7"/><stop offset="100%" stopColor="#86efac"/></linearGradient>
                  <linearGradient id="egn" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#4ade80"/><stop offset="100%" stopColor="#22c55e"/></linearGradient>
                </defs>
                <path d="M0,6 L147,6 Q153,6 153,12 L153,72 Q153,78 147,78 L0,78 L0,53 C8,53 14,48 14,40 C14,32 8,27 0,27 Z" fill="url(#egn)" transform="translate(1.5,1.5)" opacity="0.4"/>
                <path d="M0,4 L145,4 Q151,4 151,10 L151,70 Q151,76 145,76 L0,76 L0,52 C8,52 14,47 14,39 C14,31 8,26 0,26 Z" fill="url(#ggn)" stroke="#4ade80" strokeWidth="1.5"/>
                <path d="M4,8 L147,8" fill="none" stroke="white" strokeWidth="2" opacity="0.5" strokeLinecap="round"/>
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ paddingLeft: 14 }}>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-[#34A853]" style={{ boxShadow:'0 0 6px rgba(52,168,83,0.6)' }}></span>
                  <span className="text-xl font-black text-white whitespace-nowrap puzzle-text-3d" style={{ fontFamily:"'Poppins',sans-serif" }}>La TUA attivita'</span>
                </div>
                <span className="text-sm text-green-800 font-extrabold puzzle-label-3d mt-0.5">Connessa</span>
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-4 italic text-center lg:text-left">Passaci sopra e...</p>
        </div>
      </div>
      {/* Rainbow transparent puzzle piece — decorative */}
      <div className="flex justify-center mt-8 opacity-70">
        <svg width="160" height="160" viewBox="0 0 160 160" style={{ filter:'drop-shadow(0 8px 24px rgba(66,133,244,0.2))' }}>
          <defs>
            <linearGradient id="rainbow-grad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#4285F4" stopOpacity="0.6"/>
              <stop offset="25%" stopColor="#EA4335" stopOpacity="0.5"/>
              <stop offset="50%" stopColor="#FBBC04" stopOpacity="0.5"/>
              <stop offset="75%" stopColor="#34A853" stopOpacity="0.5"/>
              <stop offset="100%" stopColor="#4285F4" stopOpacity="0.6"/>
            </linearGradient>
          </defs>
          <path d="M50,10 L110,10 L110,42 C120,42 128,50 128,60 C128,70 120,78 110,78 L110,110 L78,110 L78,120 C78,130 70,138 60,138 C50,138 42,130 42,120 L42,110 L10,110 L10,78 C20,78 28,70 28,60 C28,50 20,42 10,42 L10,10 Z" fill="url(#rainbow-grad)" stroke="rgba(255,255,255,0.15)" strokeWidth="1"/>
          <path d="M55,15 L106,15" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </div>
    </div>
  </section>
);

const PWA_MOCKUP = "https://static.prod-images.emergentagent.com/jobs/c2e7986e-611e-40f0-8a3b-2eb84b9e2249/images/c4778116e83614963ed19c5cd0240f393ea60e55246f464b3d1c0fa400cbb348.png";

// PWA Install + AI Assistant marketing section
const InstallAppSection = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [canInstall, setCanInstall] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      setCanInstall(false);
    }
  };

  return (
    <section className="py-16 bg-gray-900 text-white relative overflow-hidden" data-testid="install-app-section">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 right-20 w-80 h-80 rounded-full bg-[#ff6b35]/30 blur-3xl"></div>
        <div className="absolute bottom-10 left-20 w-60 h-60 rounded-full bg-amber-400/20 blur-3xl"></div>
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          {/* Left: Phone mockup */}
          <div className="flex justify-center lg:justify-start order-2 lg:order-1">
            <div className="relative">
              <img
                src={PWA_MOCKUP}
                alt="schedabusiness App al centro tra Maps e WhatsApp"
                className="w-64 md:w-72 h-auto rounded-3xl shadow-2xl"
              />
              <div className="absolute -bottom-3 -right-3 bg-[#ff6b35] text-white px-4 py-2 rounded-2xl shadow-lg text-sm font-bold">
                <Sparkles className="w-4 h-4 inline mr-1" />
                In regalo per sempre
              </div>
            </div>
            <p className="text-center mt-8 text-white/50 text-xs italic max-w-xs mx-auto leading-relaxed">
              "Abbiamo reinventato un terzo luogo tra ufficio e Google"
            </p>
          </div>

          {/* Right: Copy */}
          <div className="order-1 lg:order-2">
            <Badge className="bg-[#ff6b35]/20 text-[#ff6b35] border-[#ff6b35]/30 mb-6 rounded-full px-4 py-1.5">
              <Smartphone className="w-4 h-4 mr-2" />
              Installa l'App schedabusiness
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-3 leading-tight font-google">
              In regalo <span className="text-[#ff6b35]">per sempre</span>
            </h2>
            <p className="text-white/60 text-sm mb-6 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 inline-block">
              Lo trovi con l'acquisto di un pacchetto: <strong className="text-white">Start</strong>, <strong className="text-white">Plus</strong>, <strong className="text-white">Rooftop</strong> o <strong className="text-white">Virtual Tour 360°</strong>
            </p>
            <p className="text-white/70 text-base md:text-lg mb-6 leading-relaxed">
              Visita il sito, scarica l'App e <strong className="text-white">posizionala fra Google e WhatsApp</strong>.
              Scrivici quando hai bisogno di assistenza e supporto.
            </p>
            <ul className="space-y-3 mb-6">
              {[
                { text: "App operativa e senza costi h24 — 7 giorni su 7", icon: Shield },
                { text: "\"Ho cambiato indirizzo, come aggiorno la scheda?\"", icon: MapPin },
                { text: "\"Ho una recensione negativa, come rispondo?\"", icon: Star },
                { text: "\"Voglio aggiungere nuove foto, come faccio?\"", icon: Camera },
              ].map(({ text, icon: Icon }, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon className="w-4 h-4 text-[#ff6b35]" />
                  </div>
                  <span className={`text-sm ${i === 0 ? 'text-white font-bold' : 'text-white/80 italic'}`}>{text}</span>
                </li>
              ))}
            </ul>
            <p className="text-white/50 text-sm italic mb-8 leading-relaxed border-l-2 border-[#ff6b35]/40 pl-4">
              Tutto questo e' possibile grazie al nostro assistente AI che non dorme mai ed ha una missione: <strong className="text-white/80">aiutarvi!</strong>
            </p>
            {canInstall ? (
              <Button
                size="lg"
                className="bg-[#ff6b35] hover:bg-[#e55a2b] text-white text-lg px-8 py-6 rounded-full font-bold"
                onClick={handleInstall}
                data-testid="install-pwa-btn"
              >
                <Download className="w-5 h-5 mr-2" />
                Installa Ora
              </Button>
            ) : (
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
                <p className="text-sm text-white/80 mb-2">
                  <strong className="text-white">Come installare:</strong>
                </p>
                <p className="text-xs text-white/60 leading-relaxed">
                  Su iPhone: Safari → Condividi → "Aggiungi a Home"<br/>
                  Su Android: Chrome → Menu (tre puntini) → "Aggiungi a Home"
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// RICCARDO ROSSI — Google Business Profile Expert / Influencer Section
const SBPDA_LOGO = "https://static.prod-images.emergentagent.com/jobs/c2e7986e-611e-40f0-8a3b-2eb84b9e2249/images/1ffc806389ed459002bd03fcc1b59acdab2351bd2139365342db26aa54e9b22e.png";

const AcademySection = () => (
  <section className="py-16 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #2d1810 0%, #1a0f0a 50%, #2d1810 100%)" }} data-testid="academy-section">
    <div className="absolute inset-0 opacity-5">
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-amber-500/30 blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full bg-red-800/20 blur-3xl"></div>
    </div>

    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">

      <div className="grid lg:grid-cols-2 gap-12 items-center">
        {/* Left: Personal Brand */}
        <div>
          <Badge className="bg-[#ff6b35]/20 text-[#ff6b35] border-[#ff6b35]/30 mb-6 rounded-full px-4 py-1.5">
            <Camera className="w-4 h-4 mr-2" />
            Street View Trusted dal 2016
          </Badge>
          <h2 className="text-3xl md:text-4xl font-black font-google leading-tight mb-2">
            <span className="text-white">Riccardo </span>
            <span className="text-[#ff6b35]">Rossi</span>
          </h2>
          <p className="text-amber-200/60 text-sm tracking-wide uppercase mb-6">Il tuo esperto Google Business Profile</p>

          <p className="text-amber-100/90 text-base md:text-lg leading-relaxed mb-6 font-google">
            Non vendo corsi. <strong className="text-white">Risolvo problemi.</strong> Da 9 anni sul campo, ogni giorno, con le attivita' che hanno bisogno di farsi trovare su Google.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            {[
              { num: "9", label: "Anni sul campo", color: "border-[#ff6b35]/40 bg-[#ff6b35]/10" },
              { num: "7K", label: "Foto pubblicate", color: "border-[#4285F4]/40 bg-[#4285F4]/10" },
              { num: "52M", label: "Visualizzazioni", color: "border-[#34A853]/40 bg-[#34A853]/10" },
            ].map((s) => (
              <div key={s.label} className={`border rounded-xl p-3 text-center ${s.color}`}>
                <p className="text-white text-2xl md:text-3xl font-black">{s.num}</p>
                <p className="text-white/50 text-[10px] mt-0.5 uppercase tracking-wide">{s.label}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <a href="https://www.schedabusiness.it" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-[#4285F4] hover:bg-[#3574d4] text-white rounded-full px-8 font-bold" data-testid="expert-cta">
                <CheckCircle className="w-5 h-5 mr-2" />
                Deducibile 100%
              </Button>
            </a>
            <Button
              size="lg" variant="outline"
              className="border-white/20 text-white hover:bg-white/10 rounded-full px-6"
              onClick={() => window.open(WA_LINK + '?text=' + encodeURIComponent('Ciao Riccardo! Vorrei informazioni sulla formazione 100% deducibile.'), '_blank')}
              data-testid="expert-wa-cta"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Scrivimi su WhatsApp
            </Button>
          </div>
        </div>

        {/* Right: Credenziali + Deducibilita' */}
        <div className="flex flex-col items-center gap-6">
          <div className="bg-white/5 border border-white/10 rounded-3xl p-6 w-full max-w-sm">
            <p className="text-white font-bold text-base mb-4 text-center">Perche' scegliere me</p>
            <ul className="space-y-3">
              {[
                "Fotografo Certificato Street View dal 2016",
                "52 milioni di visualizzazioni sulle mie foto",
                "Deducibile 100%, TAEG 0%, niente chatbot",
                "Risultati misurabili su Maps",
                "Disponibile via WhatsApp — rispondo io, non un bot",
              ].map((t, i) => (
                <li key={i} className="flex items-center gap-2.5">
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                  <span className="text-white/80 text-sm">{t}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Deduction for businesses */}
          <div className="w-full max-w-sm bg-gradient-to-r from-[#1a3a6b]/40 to-[#4285F4]/20 border border-[#4285F4]/30 rounded-2xl p-5 text-center" data-testid="detrazioni-callout">
            <TrendingUp className="w-8 h-8 text-[#4285F4] mx-auto mb-2" />
            <p className="text-[#93c5fd] text-xl font-black">DEDUCIBILE 100%</p>
            <p className="text-blue-200/60 text-sm mt-1">Per P.IVA e imprese · TAEG 0% · 3 rate PayPal</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Homepage Reviews Carousel (3+3+3)
const HomeReviewsSection = () => {
  const [page, setPage] = useState(0);
  const [auto, setAuto] = useState(true);

  const reviews = [
    { name: "Gelateria Buekke", text: "Un grande ringraziamento a Riccardo che con la sua disponibilita' e competenza ci ha aiutati a valorizzare la nostra attivita'. Grazie mille!", time: "5 anni fa", color: "bg-amber-600", initial: "G" },
    { name: "Enoteca il Salotto", text: "Grazie Riccardo per la tua professionalita' e simpatia. Non vediamo l'ora di lavorare di nuovo insieme!", time: "5 anni fa", color: "bg-red-700", initial: "E" },
    { name: "Spazio 847", text: "Abbiamo avuto il piacere di collaborare con Riccardo che ci ha aiutato a far muovere i primi passi su Google con alcune foto a 360 gradi della nostra sala polivalente Spazio 847 a Colle di Val d'Elsa. Qualita' e professionalita'.", time: "5 anni fa", color: "bg-blue-600", initial: "S" },
    { name: "Luca Gorini", text: "Complimenti a Riccardo Rossi che ci ha fatto capire l'importanza della visibilita' in un momento difficile. Negli anni di attivita' abbiamo sempre sottovalutato questo aspetto.", time: "5 anni fa", color: "bg-green-600", initial: "L" },
    { name: "Alessandra Massi", text: "Dopo aver cambiato sede, Riccardo mi ha dato la possibilita' di farmi conoscere e riconoscere! Il tutto con gentilezza e professionalita' e attraverso foto a 360 gradi che mostrano il mio nuovo negozio! Grazie Riccardo!", time: "5 anni fa", color: "bg-purple-600", initial: "A" },
    { name: "Francesco Nuzzi", text: "Abbiamo aperto il negozio da poco piu' di un anno e grazie alla professionalita' e disponibilita' di Riccardo, il negozio adesso ha un'ottima visibilita' sia sulle mappe che su Google.", time: "5 anni fa", color: "bg-teal-600", initial: "F" },
    { name: "La fenice Sapia", text: "La quattropuntozero ci ha aiutato a risolvere tutte le problematiche riguardanti Google ed i servizi ad esso annessi, portando risultati immediati all'immagine della nostra attivita'.", time: "4 anni fa", color: "bg-indigo-500", initial: "L" },
    { name: "Cristiano", text: "E' sempre un piacere lavorare con Riccardo, riesce a tirare fuori sempre il meglio da ogni scatto. Coinvolgente e geniale.", time: "6 anni fa", color: "bg-rose-500", initial: "C" },
    { name: "FILIPPO BOSCAGLI", text: "Grazie mille Riccardo. Professionalita' e gentilezza al top. Ci hai aiutato a capire meglio le potenzialita' di questo mondo.", time: "5 anni fa", color: "bg-amber-500", initial: "F" },
  ];

  const totalPages = 3;

  useEffect(() => {
    if (!auto) return;
    const t = setInterval(() => setPage((p) => (p + 1) % totalPages), 5000);
    return () => clearInterval(t);
  }, [auto]);

  const visible = reviews.slice(page * 3, page * 3 + 3);

  return (
    <section className="py-14 bg-gray-900" data-testid="home-reviews-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="bg-gray-800 rounded-xl p-4 border border-gray-700 inline-flex items-center gap-3" data-testid="home-google-card">
              <div>
                <p className="text-white font-bold text-base">SchedaBusiness.com</p>
                <div className="flex items-center gap-1">
                  <span className="text-white text-sm font-bold">5,0</span>
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />)}
                  <span className="text-gray-400 text-xs ml-1">(51)</span>
                </div>
                <p className="text-gray-500 text-[11px] mt-0.5">Fotografo commerciale a Le Grazie</p>
              </div>
            </div>
          </div>
          <h2 className="text-xl md:text-2xl font-bold text-white font-google">Cosa dicono di noi</h2>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-6"
          onMouseEnter={() => setAuto(false)} onMouseLeave={() => setAuto(true)}>
          {visible.map((r, i) => (
            <div key={`${page}-${i}`} className="bg-gray-800 rounded-xl p-4 border border-gray-700 transition-all duration-500 animate-in fade-in">
              <div className="flex items-center gap-2.5 mb-3">
                <div className={`w-9 h-9 ${r.color} rounded-full flex items-center justify-center text-white font-bold text-sm`}>{r.initial}</div>
                <div>
                  <p className="text-white font-semibold text-sm">{r.name}</p>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                    ))}
                    <span className="text-gray-500 text-[10px] ml-1">{r.time}</span>
                  </div>
                </div>
              </div>
              <p className="text-gray-300 text-xs leading-relaxed italic">"{r.text}"</p>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-center gap-2">
          {[...Array(totalPages)].map((_, i) => (
            <button key={i}
              onClick={() => { setPage(i); setAuto(false); setTimeout(() => setAuto(true), 10000); }}
              className={`transition-all duration-300 rounded-full ${i === page ? 'w-7 h-2.5 bg-[#ff6b35]' : 'w-2.5 h-2.5 bg-gray-600 hover:bg-gray-500'}`}
              data-testid={`home-review-dot-${i}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// Sezione Formazione — il meglio di schedabusiness.it integrato
const FormazioneItSection = () => (
  <section id="formazione-it" className="py-16 bg-[#0a0e1a]" data-testid="formazione-it-section">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 bg-blue-900/30 text-blue-400 border border-blue-700/30 rounded-full px-4 py-1.5 mb-6">
          <GraduationCap className="w-4 h-4" />
          <span className="text-sm font-bold">schedabusiness.it</span>
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-google">
          Formazione <span className="text-[#4285F4]">100% Detraibile</span>
        </h2>
        <p className="text-gray-400 max-w-2xl mx-auto">
          Preferisci imparare a gestire la tua scheda da solo? Il corso schedabusiness.it ti insegna tutto, passo dopo passo.
        </p>
      </div>
      <div className="max-w-lg mx-auto">
        <div className="relative bg-white/5 rounded-2xl border-2 border-[#4285F4]/40 p-8">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#4285F4] text-white text-xs font-bold px-4 py-1 rounded-full">CORSO COMPLETO</div>
          <div className="text-center mb-6">
            <GraduationCap className="w-10 h-10 text-[#4285F4] mx-auto mb-3" />
            <span className="text-4xl font-black text-white">€299</span>
            <span className="text-sm text-gray-500 block mt-1">3 rate da €99,67 · TAEG 0%</span>
          </div>
          <ul className="space-y-3 mb-6">
            {[
              "Gestione completa della tua scheda attivita'",
              "Ottimizzazione profilo per la ricerca locale",
              "Risposta professionale alle recensioni",
              "Monitoraggio e statistiche",
              "Aggiornamenti e best practice",
              "100% Detraibile nel 2027",
              "Accesso a vita al materiale",
            ].map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-sm text-gray-300">
                <Check className="w-4 h-4 text-[#4285F4] mt-0.5 shrink-0" />
                {f}
              </li>
            ))}
          </ul>
          <a
            href="https://www.schedabusiness.it"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full py-3 rounded-xl font-bold text-sm bg-[#4285F4] text-white hover:bg-[#3574d4] transition-colors block text-center"
          >
            Voglio imparare — €299
          </a>
          <div className="flex items-center justify-center gap-4 mt-4 text-xs text-gray-500">
            <span><PayPalLogo className="w-4 h-3 inline mr-1 text-gray-500" />3 rate</span>
            <span>TAEG 0%</span>
            <span>Detraibile 100%</span>
          </div>

          {/* Bundle: Formazione + Pacchetto — Cornice arcobaleno */}
          <div className="mt-6 pt-5 border-t border-white/10">
            <style>{`
              @keyframes rainbow-border-glow {
                0%, 100% { opacity: 0.3; }
                50% { opacity: 0.6; }
              }
              .rainbow-mini-glow { animation: rainbow-border-glow 3s ease-in-out infinite; }
            `}</style>
            <div className="relative">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-[#4285F4] via-[#EA4335] via-[#FBBC04] to-[#34A853] rounded-xl rainbow-mini-glow"></div>
              <div className="relative bg-[#0c1220] rounded-[10px] p-4">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <Sparkles className="w-4 h-4 text-[#FBBC04]" />
                  <span className="text-xs font-bold text-[#FBBC04] uppercase tracking-wider">Acquistali Entrambi</span>
                </div>
                <div className="space-y-3">
                  {/* Start + Formazione = €199 */}
                  <div className="flex items-center justify-between bg-white/5 rounded-lg px-4 py-3 border border-[#EA4335]/20">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#EA4335]"></span>
                      <span className="text-sm font-bold text-white">Start + Formazione</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 line-through text-sm">€299</span>
                      <span className="text-xl font-black text-[#EA4335]">€199</span>
                    </div>
                  </div>
                  {/* Plus + Formazione = €149 */}
                  <div className="flex items-center justify-between bg-white/5 rounded-lg px-4 py-3 border border-[#FBBC04]/20">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#FBBC04]"></span>
                      <span className="text-sm font-bold text-white">Plus + Formazione</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 line-through text-sm">€299</span>
                      <span className="text-xl font-black text-[#FBBC04]">€149</span>
                    </div>
                  </div>
                  {/* Rooftop = IN REGALO */}
                  <div className="flex items-center justify-between bg-[#34A853]/10 rounded-lg px-4 py-3 border-2 border-[#34A853]/30 relative">
                    <div className="absolute -top-2.5 right-3 bg-[#34A853] text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full">IN REGALO</div>
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#34A853]"></span>
                      <span className="text-sm font-bold text-white">Rooftop + Formazione</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 line-through text-sm">€299</span>
                      <span className="text-xl font-black text-[#34A853]">IN REGALO</span>
                    </div>
                  </div>
                </div>
                <a
                  href="https://www.schedabusiness.it"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 w-full py-2.5 rounded-xl font-bold text-sm bg-gradient-to-r from-[#4285F4] via-[#EA4335] via-[#FBBC04] to-[#34A853] text-white hover:opacity-90 transition-opacity block text-center"
                >
                  Scegli il tuo Bundle
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <p className="text-center text-sm text-gray-500 mt-8">
        Non vuoi fare da solo? <button onClick={() => document.getElementById('pacchetti')?.scrollIntoView({ behavior: 'smooth' })} className="text-[#ff6b35] font-bold hover:underline">Scegli l'assistenza</button> — ci pensiamo noi.
      </p>
    </div>
  </section>
);

// Google Ads Section with YouTube video
// Growth chart with arrows — visual impact section
const GrowthChartSection = () => (
  <section className="py-12 bg-[#0c1220] relative overflow-hidden" data-testid="growth-chart-section">
    <div className="max-w-5xl mx-auto px-4">
      <div className="grid lg:grid-cols-2 gap-8 items-center">
        <div>
          <div className="inline-flex items-center gap-2 bg-[#34A853]/15 text-[#34A853] px-3 py-1 rounded-full font-bold text-xs mb-4">
            <TrendingUp className="w-4 h-4" /> CRESCITA MISURABILE
          </div>
          <h2 className="text-2xl md:text-3xl font-black text-white mb-4" style={{ fontFamily:"'Poppins',sans-serif" }}>
            I numeri parlano chiaro
          </h2>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#4285F4]/15 flex items-center justify-center flex-shrink-0"><Eye className="w-5 h-5 text-[#4285F4]" /></div>
              <div><p className="text-white font-bold text-sm">+340% visite al profilo</p><p className="text-gray-500 text-xs">Media dopo ottimizzazione Google Business</p></div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#34A853]/15 flex items-center justify-center flex-shrink-0"><MapPin className="w-5 h-5 text-[#34A853]" /></div>
              <div><p className="text-white font-bold text-sm">+520% richieste indicazioni</p><p className="text-gray-500 text-xs">I clienti trovano la strada verso la TUA attivita'</p></div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#EA4335]/15 flex items-center justify-center flex-shrink-0"><Camera className="w-5 h-5 text-[#EA4335]" /></div>
              <div><p className="text-white font-bold text-sm">+870% visualizzazioni foto</p><p className="text-gray-500 text-xs">Il Virtual Tour 360 fa la differenza</p></div>
            </div>
          </div>
        </div>
        <div className="relative rounded-2xl overflow-hidden" style={{ minHeight:'280px' }}>
          <img src={AI_IMG.growth} alt="Crescita Business" className="w-full h-full object-cover rounded-2xl" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0c1220] via-transparent to-transparent"></div>
          {/* Overlay SVG arrows */}
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 280" preserveAspectRatio="none">
            <defs>
              <linearGradient id="arrow-grad-b" x1="0" y1="1" x2="0" y2="0"><stop offset="0%" stopColor="#4285F4" stopOpacity="0"/><stop offset="100%" stopColor="#4285F4" stopOpacity="0.7"/></linearGradient>
              <linearGradient id="arrow-grad-g" x1="0" y1="1" x2="0" y2="0"><stop offset="0%" stopColor="#34A853" stopOpacity="0"/><stop offset="100%" stopColor="#34A853" stopOpacity="0.7"/></linearGradient>
            </defs>
            <path d="M50,260 Q150,180 200,120 T350,30" fill="none" stroke="url(#arrow-grad-b)" strokeWidth="3" strokeLinecap="round"/>
            <polygon points="345,20 360,35 340,38" fill="#4285F4" opacity="0.8"/>
            <path d="M80,260 Q180,200 240,150 T370,50" fill="none" stroke="url(#arrow-grad-g)" strokeWidth="2" strokeLinecap="round" strokeDasharray="6 4"/>
            <polygon points="365,40 380,55 358,56" fill="#34A853" opacity="0.6"/>
          </svg>
        </div>
      </div>
    </div>
  </section>
);

const GoogleAdsSection = () => (
  <section className="py-16 bg-[#0c1220]" data-testid="google-ads-section">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-10 items-center">
        {/* Left: Copy */}
        <div>
          <Badge className="bg-blue-900/30 text-blue-400 border-blue-700/30 mb-6 rounded-full px-4 py-1.5">
            <Target className="w-4 h-4 mr-2" />
            Google Ads
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight font-google">
            La TUA scheda Google e' solo <span className="text-[#ff6b35]">l'inizio</span>
          </h2>
          <p className="text-gray-300 text-base md:text-lg mb-6 leading-relaxed">
            Una scheda ottimizzata attira clienti organicamente. Ma se vuoi
            <strong className="text-white"> accelerare i risultati</strong> e raggiungere chi
            sta cercando proprio i TUOI servizi in questo momento, le
            <strong className="text-white"> Google Ads</strong> fanno la differenza.
          </p>
          <p className="text-gray-500 text-sm mb-8 leading-relaxed">
            Guarda questo video per capire come funzionano e perche' possono cambiare
            il gioco per la TUA attivita'. Poi, se vorrai, ne parliamo insieme.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              size="lg"
              className="bg-[#ff6b35] hover:bg-[#e55a2b] text-white rounded-full px-8"
              onClick={() => window.open(WA_LINK + '?text=' + encodeURIComponent('Ciao Riccardo! Ho visto il video sulle Google Ads e vorrei saperne di piu per la mia attivita.'), '_blank')}
              data-testid="google-ads-cta"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Parliamone su WhatsApp
            </Button>
          </div>
        </div>

        {/* Right: YouTube Video */}
        <div className="relative">
          <div className="bg-white/5 rounded-2xl overflow-hidden border border-white/10">
            <div className="aspect-video">
              <iframe
                src="https://www.youtube-nocookie.com/embed/fUVpkf_Rb38"
                title="L'importanza delle Google Ads per la TUA attivita"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
              ></iframe>
            </div>
            <div className="p-4 bg-white/5">
              <p className="text-sm text-gray-400">
                <strong className="text-white">Scopri di piu'</strong> — Come le Google Ads possono portare nuovi clienti alla TUA attivita'
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);


const EcologiaDigitaleCTA = ({ onOpen }) => (
  <section className="py-12 relative overflow-hidden" data-testid="ecologia-cta-section">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="bg-gradient-to-br from-[#34A853]/10 to-[#0c1220] rounded-2xl border border-[#34A853]/20 p-6 md:p-8 flex flex-col md:flex-row items-center gap-6">
        <div className="w-14 h-14 bg-[#34A853] rounded-2xl flex items-center justify-center flex-shrink-0">
          <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 8C17 8 15 5 12 2C9 5 7 8 7 8" /><path d="M7 8C3 14 5 20 12 22C19 20 21 14 17 8" /><path d="M12 22V12" /><path d="M7.5 14.5L12 12L16.5 14.5" />
          </svg>
        </div>
        <div className="flex-1 text-center md:text-left">
          <h3 className="text-white font-black text-lg mb-1">Lo sapevi che dire "ok" via email inquina?</h3>
          <p className="text-gray-400 text-sm">Noi siamo dark by design, per il pianeta. Scopri come la tua vita digitale impatta l'ambiente — e cosa puoi fare in 5 secondi.</p>
        </div>
        <button
          onClick={onOpen}
          className="bg-[#34A853] hover:bg-[#2d9249] text-white font-bold px-6 py-3 rounded-full transition-all hover:scale-105 flex items-center gap-2 flex-shrink-0 text-sm"
          data-testid="ecologia-cta-btn"
        >
          Scopri perche <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  </section>
);

export default function HomePage() {
  const [splashDone, setSplashDone] = useState(() => !!localStorage.getItem("sbp-splash-seen"));
  const [showEcologia, setShowEcologia] = useState(false);
  const handleSplashComplete = useCallback(() => setSplashDone(true), []);

  return (
    <>
      {!splashDone && <IntroSplash onComplete={handleSplashComplete} />}
      <HeroSection />
      <ScrollToTop />
      <ScarsitaSection />
      <FourDoorsSection />
      <BrandDivider />
      <VirtualTourSection />
      <HomeReviewsSection />
      <BrandDivider />
      <ChiFaLeFotoSection />
      <ProofSection />
      <BrandDivider />
      <GrowthChartSection />
      <FormazioneItSection />
      <ContactSection />
      <EcologiaDigitaleArticle isOpen={showEcologia} onClose={() => setShowEcologia(false)} />
    </>
  );
}
